import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
    ArrowDown,
    ArrowRight,
    ArrowDownRight,
    ArrowUpRight,
    BarChart3,
    Bell,
    Boxes,
    CalendarDays,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Clock3,
    Database,
    FileSpreadsheet,
    LineChart,
    Lock,
    MapPinned,
    Menu,
    PackageCheck,
    Play,
    Plug,
    Plus,
    RefreshCw,
    ShieldCheck,
    ShoppingCart,
    Sparkles,
    Sun,
    Users,
    X,
    Zap,
} from 'lucide-react';

const BRAND = '#E55C1A';
const DEMO_EMAIL = 'info@commetriq.de';
const LANDING_API_URL = (() => {
    const configuredUrl = (process.env.REACT_APP_REPORTS_API_URL || '').trim();
    if (configuredUrl) return configuredUrl.replace(/\/$/, '');
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080';
})();

const LEAD_FORM_INITIAL = {
    name: '',
    email: '',
    shopName: '',
    usesJtl: 'unclear',
    orderVolume: 'unclear',
    channels: '',
    message: '',
    website: '',
};

const orderVolumeOptions = [
    ['0-500', 'Bis 500 Bestellungen / Monat'],
    ['501-2500', '501 - 2.500 Bestellungen / Monat'],
    ['2501-10000', '2.501 - 10.000 Bestellungen / Monat'],
    ['10000+', 'Mehr als 10.000 Bestellungen / Monat'],
    ['unclear', 'Noch unklar'],
];

function cx(...classes) {
    return classes.filter(Boolean).join(' ');
}

const OptimizedImage = ({ src, alt, compactWidth, width, sizes = '100vw', ...imageProps }) => {
    const baseSrc = String(src || '').replace(/\.png$/i, '');
    const fullWidth = Number(width) || compactWidth;
    const buildSrcSet = (extension) => compactWidth && fullWidth
        ? `${baseSrc}-${compactWidth}.${extension} ${compactWidth}w, ${baseSrc}.${extension} ${fullWidth}w`
        : `${baseSrc}.${extension}`;

    return (
        <picture className="contents">
            <source type="image/avif" srcSet={buildSrcSet('avif')} sizes={sizes} />
            <source type="image/webp" srcSet={buildSrcSet('webp')} sizes={sizes} />
            <img src={src} alt={alt} width={width} {...imageProps} />
        </picture>
    );
};

const formatNumber = (value, decimals) => value.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
});

const AnimatedNumber = ({ value, suffix = '', prefix = '', decimals = 0, loop = false }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        let frame;
        let loopTimer;
        const duration = 1100;
        const start = performance.now();

        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplayValue(value * eased);

            if (progress < 1) {
                frame = requestAnimationFrame(tick);
                return;
            }

            setDisplayValue(value);
            if (!loop) {
                return;
            }

            loopTimer = window.setTimeout(() => {
                const loopStart = performance.now();
                const loop = (loopNow) => {
                    const phase = ((loopNow - loopStart) % 3200) / 3200;
                    const wave = (Math.sin(phase * Math.PI * 2 - Math.PI / 2) + 1) / 2;
                    setDisplayValue(value * (0.99 + wave * 0.01));
                    frame = requestAnimationFrame(loop);
                };
                frame = requestAnimationFrame(loop);
            }, 500);
        };

        frame = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(frame);
            window.clearTimeout(loopTimer);
        };
    }, [value, loop]);

    return (
        <>
            {prefix}
            {formatNumber(displayValue, decimals)}
            {suffix}
        </>
    );
};

const Pill = ({ children }) => (
    <p className="text-sm font-semibold uppercase tracking-[0.32em] text-orange-600">
        {children}
    </p>
);

const LeadFormModal = ({ open, onClose }) => {
    const [form, setForm] = useState(LEAD_FORM_INITIAL);
    const [state, setState] = useState({ submitting: false, error: '', success: false });

    useEffect(() => {
        if (open) return undefined;
        const resetTimer = window.setTimeout(() => {
            setForm(LEAD_FORM_INITIAL);
            setState({ submitting: false, error: '', success: false });
        }, 180);
        return () => window.clearTimeout(resetTimer);
    }, [open]);

    const updateField = (field) => (event) => {
        setForm(current => ({ ...current, [field]: event.target.value }));
        setState(current => ({ ...current, error: '' }));
    };

    const submitLead = async (event) => {
        event.preventDefault();
        if (state.submitting) return;

        setState({ submitting: true, error: '', success: false });

        try {
            const sourcePath = typeof window === 'undefined'
                ? '/'
                : `${window.location.pathname}${window.location.search}`;
            const response = await fetch(`${LANDING_API_URL}/api/landing-leads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, sourcePath }),
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.error || 'Anfrage konnte gerade nicht gesendet werden.');
            }

            window.commetriqTrackLead?.();
            setState({ submitting: false, error: '', success: true });
        } catch (err) {
            setState({
                submitting: false,
                error: err.message || 'Anfrage konnte gerade nicht gesendet werden.',
                success: false,
            });
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 px-4 py-6 backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="lead-form-title"
                        className="relative max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_28px_90px_rgba(15,23,42,0.28)] sm:p-8"
                        initial={{ opacity: 0, y: 24, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.98 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={state.submitting}
                            aria-label="Formular schließen"
                            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        {state.success ? (
                            <div className="pr-8">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                                    <Check className="h-6 w-6" strokeWidth={3} />
                                </div>
                                <h2 id="lead-form-title" className="mt-5 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
                                    Danke, wir melden uns zeitnah.
                                </h2>
                                <p className="mt-4 text-base leading-7 text-slate-600">
                                    Deine Anfrage ist angekommen. Wir prüfen kurz die passende Einrichtung für deinen Shop und melden uns per E-Mail.
                                </p>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="mt-7 inline-flex items-center justify-center rounded-lg bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
                                >
                                    Schließen
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="pr-10">
                                    <Pill>Testzugang anfragen</Pill>
                                    <h2 id="lead-form-title" className="mt-5 text-3xl font-semibold tracking-[-0.04em] text-slate-950">
                                        Sag uns kurz, wie dein Shop arbeitet.
                                    </h2>
                                    <p className="mt-3 text-sm leading-6 text-slate-500">
                                        Wir melden uns per E-Mail und besprechen, welche Auftragskosten du heute prüfen möchtest. Die Demo verwendet Beispieldaten; einen möglichen Test mit eigenen Daten klären wir erst danach.
                                    </p>
                                </div>

                                <form onSubmit={submitLead} className="mt-7 grid gap-4">
                                    <div className="hidden" aria-hidden="true">
                                        <label>
                                            Website
                                            <input
                                                type="text"
                                                name="website"
                                                value={form.website}
                                                onChange={updateField('website')}
                                                tabIndex={-1}
                                                autoComplete="off"
                                            />
                                        </label>
                                    </div>

                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <label className="grid gap-2 text-sm font-semibold text-slate-700">
                                            Name
                                            <input
                                                required
                                                type="text"
                                                value={form.name}
                                                onChange={updateField('name')}
                                                className="rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-950 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                                                placeholder="Max Mustermann"
                                            />
                                        </label>
                                        <label className="grid gap-2 text-sm font-semibold text-slate-700">
                                            E-Mail
                                            <input
                                                required
                                                type="email"
                                                value={form.email}
                                                onChange={updateField('email')}
                                                className="rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-950 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                                                placeholder="name@shop.de"
                                            />
                                        </label>
                                    </div>

                                    <label className="grid gap-2 text-sm font-semibold text-slate-700">
                                        Shopname
                                        <input
                                            required
                                            type="text"
                                            value={form.shopName}
                                            onChange={updateField('shopName')}
                                            className="rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-950 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                                            placeholder="Dein Shop"
                                        />
                                    </label>

                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <label className="grid gap-2 text-sm font-semibold text-slate-700">
                                            Nutzt ihr JTL-Wawi?
                                            <select
                                                value={form.usesJtl}
                                                onChange={updateField('usesJtl')}
                                                className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-950 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                                            >
                                                <option value="yes">Ja</option>
                                                <option value="no">Nein</option>
                                                <option value="unclear">Noch unklar</option>
                                            </select>
                                        </label>
                                        <label className="grid gap-2 text-sm font-semibold text-slate-700">
                                            Monatliches Bestellvolumen
                                            <select
                                                value={form.orderVolume}
                                                onChange={updateField('orderVolume')}
                                                className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-950 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                                            >
                                                {orderVolumeOptions.map(([value, label]) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>

                                    <label className="grid gap-2 text-sm font-semibold text-slate-700">
                                        Verkaufskanäle
                                        <input
                                            type="text"
                                            value={form.channels}
                                            onChange={updateField('channels')}
                                            className="rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-950 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                                            placeholder="Amazon, eBay, Shopify, eigener Shop"
                                        />
                                    </label>

                                    <label className="grid gap-2 text-sm font-semibold text-slate-700">
                                        Optionale Nachricht
                                        <textarea
                                            value={form.message}
                                            onChange={updateField('message')}
                                            rows={4}
                                            className="resize-none rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-950 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                                            placeholder="Welche Zahl oder welchen Auftrag möchtest du besser verstehen?"
                                        />
                                    </label>

                                    {state.error && (
                                        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                                            {state.error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={state.submitting}
                                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-6 py-4 text-sm font-semibold text-white shadow-[0_18px_36px_rgba(229,92,26,0.22)] transition-all hover:-translate-y-0.5 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                                    >
                                        {state.submitting ? 'Anfrage wird gesendet...' : 'Anfrage senden'}
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                    <p className="text-xs leading-5 text-slate-500">
                                        Mit dem Absenden verarbeiten wir deine Angaben zur Bearbeitung deiner Gesprächsanfrage. Details findest du in der <a href="/datenschutz" className="font-semibold text-slate-700 underline underline-offset-2 hover:text-orange-700">Datenschutzerklärung</a>.
                                    </p>
                                </form>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const TrendLabel = ({ value, tone = 'green', direction = 'up' }) => {
    const Icon = direction === 'down' ? ArrowDownRight : ArrowUpRight;
    const isPositive = tone === 'green';

    return (
        <span className={cx(
            'inline-flex items-center gap-1 text-xs font-semibold',
            isPositive ? 'text-emerald-600' : 'text-red-600'
        )}>
            <Icon className="h-3.5 w-3.5" strokeWidth={3} />
            {value}
        </span>
    );
};

const SectionHeader = ({ pill, title, text, center = false }) => (
    <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.65, ease: 'easeOut' }}
        className={cx(center ? 'mx-auto text-center' : '', 'max-w-3xl')}
    >
        {pill && <Pill>{pill}</Pill>}
        <h2 className="mt-6 text-4xl font-semibold leading-[1.06] tracking-[-0.055em] text-slate-950 sm:text-5xl lg:text-6xl">
            {title}
        </h2>
        {text && <p className="mt-5 text-base leading-8 text-slate-500">{text}</p>}
    </motion.div>
);

const sectionReveal = {
    initial: { opacity: 0, y: 46 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-110px' },
    transition: { duration: 0.75, ease: 'easeOut' },
};

const subtleContentReveal = {
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '0px 0px -40px 0px' },
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
};

const MiniLine = ({ points, color = BRAND, height = 76, strokeWidth = 4, muted = false }) => {
    const width = 320;
    const chartId = useMemo(() => `chart-${Math.random().toString(36).slice(2)}`, []);
    const max = Math.max(...points);
    const min = Math.min(...points);
    const range = Math.max(max - min, 1);
    const coords = points.map((point, index) => {
        const x = (index / (points.length - 1)) * width;
        const y = height - ((point - min) / range) * (height - 16) - 8;
        return { x, y };
    });
    const path = coords.reduce((acc, point, index) => {
        if (index === 0) {
            return `M ${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
        }

        const previous = coords[index - 1];
        const controlX = previous.x + (point.x - previous.x) * 0.5;
        return `${acc} C ${controlX.toFixed(1)} ${previous.y.toFixed(1)}, ${controlX.toFixed(1)} ${point.y.toFixed(1)}, ${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
    }, '');
    const first = coords[0];
    const last = coords[coords.length - 1];
    const areaPath = `${path} L ${last.x.toFixed(1)} ${height} L ${first.x.toFixed(1)} ${height} Z`;

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full overflow-visible">
            <defs>
                <linearGradient id={`${chartId}-fill`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={muted ? 0.14 : 0.24} />
                    <stop offset="72%" stopColor={color} stopOpacity="0.04" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
                <filter id={`${chartId}-glow`} x="-20%" y="-40%" width="140%" height="180%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>
            {[0.25, 0.5, 0.75].map((line) => (
                <line
                    key={line}
                    x1="0"
                    x2={width}
                    y1={height * line}
                    y2={height * line}
                    stroke="rgba(148,163,184,0.16)"
                    strokeDasharray="4 8"
                />
            ))}
            <motion.path
                d={areaPath}
                fill={`url(#${chartId}-fill)`}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
            />
            <motion.path
                d={path}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={`url(#${chartId}-glow)`}
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: muted ? 0.7 : 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.05, ease: 'easeOut' }}
            />
            <motion.circle
                cx={last.x}
                cy={last.y}
                r={strokeWidth + 1}
                fill="white"
                stroke={color}
                strokeWidth="3"
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.85, duration: 0.35, ease: 'easeOut' }}
            />
        </svg>
    );
};

const PulsingBars = ({ values, className = 'h-24' }) => (
    <div className={cx('relative flex items-end gap-2', className)}>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-300/80 to-transparent" />
        {values.map((height, index) => (
            <motion.div
                key={index}
                initial={{ height: 0, opacity: 0 }}
                whileInView={{ height: `${height}%`, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06, duration: 0.78, ease: [0.2, 0.8, 0.2, 1] }}
                className="relative flex-1 overflow-hidden rounded-t-xl"
                style={{
                    background: index % 3 === 0
                        ? 'linear-gradient(180deg,rgba(229,92,26,0.30),rgba(229,92,26,0.08))'
                        : 'linear-gradient(180deg,#E55C1A 0%,#F39A6D 100%)',
                    boxShadow: index % 3 === 0 ? 'none' : '0 10px 24px rgba(229,92,26,0.20)',
                }}
            >
                <motion.span
                    className="absolute inset-x-0 top-0 h-1 rounded-full bg-white/55"
                    animate={{ opacity: [0.35, 0.75, 0.35] }}
                    transition={{ duration: 2.6, delay: index * 0.08, repeat: Infinity, ease: 'easeInOut' }}
                />
            </motion.div>
        ))}
    </div>
);

const DonutChart = ({ segments, size = 148, stroke = 18, label = 'Gesamt', value = '68,9k' }) => {
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full -rotate-90">
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(226,232,240,0.9)" strokeWidth={stroke} />
                {segments.map((segment, index) => {
                    const dash = (segment.value / 100) * circumference;
                    const circle = (
                        <motion.circle
                            key={segment.label}
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke={segment.color}
                            strokeWidth={stroke}
                            strokeLinecap="round"
                            strokeDasharray={`${dash} ${circumference - dash}`}
                            strokeDashoffset={-offset}
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.12 + index * 0.1, ease: 'easeOut' }}
                        />
                    );
                    offset += dash;
                    return circle;
                })}
            </svg>
            <div className="absolute inset-[22%] rounded-full bg-white shadow-[inset_0_0_0_1px_rgba(226,232,240,0.9)]" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">{value}</p>
            </div>
        </div>
    );
};

const FlowArrow = ({ d, progress, color = BRAND, width = 2.8 }) => {
    const lineOpacity = useTransform(progress, [0, 0.05, 1], [0, 1, 1]);
    const highlightOpacity = useTransform(progress, [0, 0.12, 1], [0, 0, 0.75]);

    return (
    <>
        <motion.path
            d={d}
            fill="none"
            stroke={color}
            strokeWidth={width}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pathLength: progress, opacity: lineOpacity }}
        />
        <motion.path
            d={d}
            fill="none"
            stroke="rgba(255,255,255,0.75)"
            strokeWidth={Math.max(width - 0.9, 1.4)}
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="0.08"
            style={{ pathOffset: progress, opacity: highlightOpacity }}
        />
    </>
    );
};

const RegionHeatMap = () => {
    const regions = [
        { d: 'M116 22L166 40L157 86L111 78L92 46Z', value: 0.28, label: 'Nord' },
        { d: 'M76 72L118 80L126 126L82 142L52 110Z', value: 0.44, label: 'West' },
        { d: 'M129 91L175 96L190 146L151 176L126 126Z', value: 0.82, label: 'Ost' },
        { d: 'M81 150L128 132L153 180L128 224L74 206Z', value: 0.62, label: 'Südwest' },
        { d: 'M158 183L197 151L228 190L203 236L139 229Z', value: 0.36, label: 'Südost' },
    ];

    return (
        <div className="relative h-full rounded-[1.35rem] bg-gradient-to-br from-white via-orange-50/45 to-slate-50 p-4">
            <svg viewBox="0 0 280 260" className="h-full w-full">
                {regions.map((region, index) => (
                    <motion.path
                        key={region.label}
                        d={region.d}
                        fill={`rgba(229,92,26,${region.value})`}
                        stroke="white"
                        strokeWidth="4"
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.08, ease: 'easeOut' }}
                    />
                ))}
                {[
                    [156, 115, 'NDS'],
                    [109, 160, 'NRW'],
                    [184, 198, 'BAY'],
                ].map(([cx, cy, label], index) => (
                    <motion.g
                        key={label}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.35 + index * 0.1, duration: 0.45, ease: 'easeOut' }}
                    >
                        <circle cx={cx} cy={cy} r="13" fill="#111827" />
                        <text x={cx} y={cy + 4} textAnchor="middle" fontSize="9" fontWeight="700" fill="white">{label}</text>
                    </motion.g>
                ))}
            </svg>
            <div className="absolute bottom-4 left-4 right-4 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-white/70 bg-white/75 p-3 shadow-sm backdrop-blur">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Wiederkäufer</p>
                    <p className="mt-1 text-lg font-semibold text-slate-950">38%</p>
                </div>
                <div className="rounded-2xl border border-white/70 bg-white/75 p-3 shadow-sm backdrop-blur">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Top Region</p>
                    <p className="mt-1 text-lg font-semibold text-slate-950">NDS</p>
                </div>
            </div>
        </div>
    );
};

const ModuleSliderControls = ({ onPrevious, onNext, progress }) => (
    <div className="mt-7 flex items-center gap-3">
        <button
            type="button"
            aria-label="Vorherige Module"
            onClick={onPrevious}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-700 shadow-sm backdrop-blur transition-all hover:-translate-y-0.5 hover:border-orange-200 hover:text-orange-600 hover:shadow-md"
        >
            <ChevronLeft className="h-5 w-5" strokeWidth={2.6} />
        </button>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/70 shadow-[inset_0_0_0_1px_rgba(226,232,240,0.9)]">
            <motion.div
                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-300"
                animate={{ width: `${Math.max(18, progress * 100)}%` }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
            />
        </div>
        <button
            type="button"
            aria-label="Weitere Module"
            onClick={onNext}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-700 shadow-sm backdrop-blur transition-all hover:-translate-y-0.5 hover:border-orange-200 hover:text-orange-600 hover:shadow-md"
        >
            <ChevronRight className="h-5 w-5" strokeWidth={2.6} />
        </button>
    </div>
);

const DeferredModuleVisual = ({ children, sliderRef, label }) => {
    const containerRef = useRef(null);
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
            setShouldRender(true);
            return undefined;
        }

        const observer = new IntersectionObserver((entries) => {
            if (!entries.some(entry => entry.isIntersecting)) return;
            setShouldRender(true);
            observer.disconnect();
        }, {
            root: sliderRef.current || null,
            rootMargin: '0px 45%',
            threshold: 0.01,
        });

        observer.observe(container);
        return () => observer.disconnect();
    }, [sliderRef]);

    return (
        <div
            ref={containerRef}
            className="h-[240px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_8px_22px_rgba(15,23,42,0.035)]"
        >
            {shouldRender ? children : (
                <div className="flex h-full items-center justify-center bg-gradient-to-b from-white to-slate-50/70 px-6 text-center">
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-300">{label}</span>
                </div>
            )}
        </div>
    );
};

const PLATFORM_TOUR_PREVIEW_START = 7.4;

const PlatformTourSection = () => {
    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const [tourStarted, setTourStarted] = useState(false);
    const isInView = useInView(containerRef, { once: true, amount: 0.35 });

    useEffect(() => {
        if (!isInView || tourStarted) return undefined;

        const video = videoRef.current;
        if (!video) return undefined;
        const playPreview = () => {
            video.muted = true;
            video.loop = false;
            video.currentTime = PLATFORM_TOUR_PREVIEW_START;
            video.play().catch(() => {});
        };

        if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
            playPreview();
        } else {
            video.addEventListener('loadedmetadata', playPreview, { once: true });
        }
        return () => video.removeEventListener('loadedmetadata', playPreview);
    }, [isInView, tourStarted]);

    const startTour = () => {
        const video = videoRef.current;
        if (!video) return;
        video.pause();
        video.currentTime = 0;
        video.loop = false;
        video.muted = false;
        setTourStarted(true);
        video.play().catch(() => {
            // Leave native controls available if playback needs another user action.
        });
    };

    const returnToPreview = () => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = true;
        video.loop = false;
        video.currentTime = PLATFORM_TOUR_PREVIEW_START;
        setTourStarted(false);
        video.play().catch(() => {});
    };

    return (
        <section id="plattformtour" className="relative isolate scroll-mt-20 overflow-hidden bg-[#fffaf6] px-4 py-16 sm:px-[5.5vw] md:py-24">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{ background: 'radial-gradient(ellipse 58% 48% at 50% 62%, rgba(247,149,91,0.14), transparent 76%), linear-gradient(180deg, #fffaf6 0%, #fffdfb 56%, #fff9f5 100%)' }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute right-0 top-0 h-64 w-72 sm:h-80 sm:w-[430px]"
                style={{
                    backgroundImage: 'radial-gradient(rgba(145,96,64,0.68) 1.5px, transparent 1.7px)',
                    backgroundSize: '20px 20px',
                    maskImage: 'radial-gradient(ellipse at 100% 0%, black 40%, transparent 92%)',
                }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 h-64 w-72 sm:h-80 sm:w-[430px]"
                style={{
                    backgroundImage: 'radial-gradient(rgba(205,74,21,0.56) 1.5px, transparent 1.7px)',
                    backgroundSize: '20px 20px',
                    maskImage: 'radial-gradient(ellipse at 0% 100%, black 40%, transparent 92%)',
                }}
            />
            <div className="relative mx-auto max-w-[1160px]">
                <h2 className="sr-only">commetriq Plattformtour</h2>
                <div
                    ref={containerRef}
                    className="relative overflow-hidden rounded-xl border border-slate-200 bg-[#0D121C] shadow-[0_16px_48px_rgba(15,23,42,0.10)]"
                    style={{ aspectRatio: '799 / 540' }}
                >
                    <video
                        ref={videoRef}
                        className="absolute inset-0 h-full w-full object-cover"
                        src="/commetriq-platform-tour.mp4?v=4"
                        muted={!tourStarted}
                        controls={tourStarted}
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={(event) => {
                            if (!tourStarted) event.currentTarget.currentTime = PLATFORM_TOUR_PREVIEW_START;
                        }}
                        onEnded={returnToPreview}
                        aria-label="commetriq Plattformtour"
                    >
                        Dein Browser unterstützt dieses Video nicht.
                    </video>
                    {!tourStarted && (
                        <button
                            type="button"
                            onClick={startTour}
                            className="group absolute left-1/2 top-1/2 inline-flex w-[min(90%,340px)] -translate-x-1/2 -translate-y-1/2 items-center justify-between gap-2 rounded-xl bg-orange-600 px-3 py-2 text-left text-white shadow-[0_14px_34px_rgba(125,42,9,0.28)] transition-colors hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-orange-600 sm:gap-4 sm:px-6 sm:py-4"
                            aria-label="Plattformtour im eingebetteten Video starten"
                        >
                            <span>
                                <span className="block text-sm font-semibold sm:text-lg">Plattformtour ansehen</span>
                                <span className="mt-0.5 block text-[11px] text-orange-100 sm:text-sm">Mit Ton ansehen · 1:47 Min.</span>
                            </span>
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-orange-600 transition-transform group-hover:scale-105 sm:h-12 sm:w-12">
                                <Play className="ml-0.5 h-5 w-5 fill-current" aria-hidden="true" />
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
};

const DailyProblemBentoSection = () => {
    const answers = [
        [BarChart3, 'Was bleibt nach Kosten wirklich übrig?', 'Rohertrag statt nur Umsatz.'],
        [Boxes, 'Welche Artikel werden heute kritisch?', 'Low Stock erkennen, bevor es teuer wird.'],
        [RefreshCw, 'Wo verliert der Shop gerade Marge?', 'Retouren und Kanäle im Blick behalten.'],
    ];

    return (
        <motion.section
            {...sectionReveal}
            viewport={{ once: true, margin: '0px 0px -28% 0px', amount: 0.22 }}
            id="problem"
            className="commetriq-deferred-section commetriq-deferred-section-problem relative scroll-mt-28 overflow-hidden bg-[#fffdf9] px-4 pb-24 pt-28 md:pb-28 md:pt-32 lg:px-10 xl:px-12"
        >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(229,92,26,0.055),transparent_34%),radial-gradient(circle_at_8%_86%,rgba(229,92,26,0.035),transparent_30%)]" />
            <div className="pointer-events-none absolute -bottom-28 -left-20 h-[420px] w-[420px] opacity-[0.28]"
                style={{
                    backgroundImage: 'linear-gradient(30deg, rgba(229,92,26,0.18) 1px, transparent 1px), linear-gradient(150deg, rgba(229,92,26,0.12) 1px, transparent 1px)',
                    backgroundSize: '26px 26px',
                }}
            />
            <div className="pointer-events-none absolute -right-12 -top-24 h-[380px] w-[380px] opacity-[0.24]"
                style={{
                    backgroundImage: 'linear-gradient(30deg, rgba(229,92,26,0.18) 1px, transparent 1px), linear-gradient(150deg, rgba(229,92,26,0.12) 1px, transparent 1px)',
                    backgroundSize: '30px 30px',
                }}
            />

            <div className="commetriq-daily-problem-grid relative mx-auto grid max-w-[1600px] gap-12 xl:grid-cols-[0.36fr_0.64fr] xl:items-start 2xl:gap-14">
                <div className="max-w-xl xl:pt-14">
                    <div className="inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.32em] text-orange-600">
                        <span className="h-2.5 w-2.5 rounded-full bg-orange-600" />
                        Dein täglicher Überblick
                    </div>
                    <h2 className="mt-7 text-5xl font-semibold leading-[1.08] tracking-[-0.055em] text-slate-950 md:text-6xl">
                        Jeden Morgen wissen, was im Shop zählt.
                    </h2>
                    <p className="mt-7 max-w-lg text-lg leading-8 text-slate-600">
                        commetriq priorisiert die Signale aus Umsatz, Marge, Lager und Retouren, damit du direkt siehst, was heute Aufmerksamkeit braucht.
                    </p>

                    <div className="mt-11 divide-y divide-slate-200">
                        {answers.map(([Icon, title, text], index) => (
                            <motion.div
                                key={title}
                                initial={{ opacity: 0, x: -16 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.08, duration: 0.5, ease: 'easeOut' }}
                                className="flex items-center gap-5 py-6 first:pt-0"
                            >
                                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 shadow-[0_18px_40px_rgba(229,92,26,0.1)]">
                                    <Icon className="h-8 w-8" strokeWidth={2.2} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold tracking-[-0.035em] text-slate-950">{title}</h3>
                                    <p className="mt-1 text-lg text-slate-600">{text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24, scale: 0.985 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.62, ease: 'easeOut' }}
                    className="w-full max-w-[1080px] justify-self-end pt-10 xl:pt-14"
                >
                    <OptimizedImage
                        src="/section2-full-crop.png"
                        alt="commetriq Bento-Übersicht mit Dashboard, Tageswerten, Low Stock und Marge-Retouren-Analyse"
                        className="h-auto w-full select-none drop-shadow-[0_28px_76px_rgba(15,23,42,0.1)]"
                        loading="lazy"
                        decoding="async"
                        compactWidth={900}
                        sizes="(min-width: 1280px) 64vw, 100vw"
                        width="1337"
                        height="915"
                    />
                </motion.div>
            </div>
        </motion.section>
    );
};
const TrustStrip = () => {
    const systems = [
        ['JTL Wawi', null],
        ['Amazon', '/amazon.png'],
        ['eBay', '/ebay.png'],
        ['Shop', null],
        ['DHL', null],
        ['CSV Reports', null],
    ];
    const proof = [
        ['Wawi-Datenbasis', 'Aufträge, Artikel, Lager und Retouren zentral auswerten.', Database],
        ['Kanäle zusammenführen', 'Amazon, eBay und Shop nach Umsatz und Marge vergleichen.', ShoppingCart],
        ['Profit priorisieren', 'Rohertrag, Topseller und Risiken als klare Tagesaufgaben.', Sparkles],
        ['Sicher skalieren', 'Mandanten, Rollen und Reports sauber pro Shop trennen.', ShieldCheck],
    ];

    return (
        <section className="relative overflow-hidden border-y border-slate-200 bg-white px-4 py-16">
            <div className="mx-auto max-w-7xl">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.58, ease: 'easeOut' }}
                    className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6"
                >
                    {systems.map(([label, src]) => (
                        <div key={label} className="flex h-10 min-w-[104px] items-center justify-center text-center text-lg font-semibold tracking-[-0.03em] text-slate-950/70 grayscale transition hover:grayscale-0">
                            {src ? (
                                <img src={src} alt={label} className="max-h-8 max-w-[105px] object-contain opacity-70" loading="lazy" />
                            ) : (
                                label
                            )}
                        </div>
                    ))}
                </motion.div>

                <div className="mx-auto mt-16 max-w-3xl text-center">
                    <h2 className="text-4xl font-semibold leading-[1.08] tracking-[-0.05em] text-slate-950 sm:text-5xl">
                        Verbinde Wawi, Marktplätze und Shopdaten in einem Profit-Cockpit.
                    </h2>
                    <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-500">
                        commetriq macht aus verstreuten Kennzahlen eine klare Sicht auf Umsatz, Marge, Bestand und Retouren.
                    </p>
                </div>

                <div className="mt-12 grid border-y border-slate-200 md:grid-cols-4">
                    {proof.map(([title, text, Icon], index) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.08, duration: 0.5, ease: 'easeOut' }}
                            className="border-b border-slate-200 px-5 py-7 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0"
                        >
                            <Icon className="h-6 w-6 text-orange-600" strokeWidth={2.5} />
                            <p className="mt-5 text-base font-semibold text-slate-950">{title}</p>
                            <p className="mt-2 text-sm leading-6 text-slate-500">{text}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const PainSection = () => {
    const questions = [
        {
            icon: FileSpreadsheet,
            eyebrow: 'Umsatz',
            title: 'Was bleibt nach Kosten wirklich übrig?',
            metric: '31,8%',
            hint: 'Rohertrag sichtbar statt nur Umsatz.',
        },
        {
            icon: PackageCheck,
            eyebrow: 'Bestand',
            title: 'Welche Artikel werden heute kritisch?',
            metric: '12 SKUs',
            hint: 'Low Stock vor dem Tagesgeschäft erkennen.',
        },
        {
            icon: RefreshCw,
            eyebrow: 'Retouren',
            title: 'Wo verliert der Shop gerade Marge?',
            metric: '4,2%',
            hint: 'Retourenquote und Gründe im Blick behalten.',
        },
    ];
    return (
        <motion.section {...sectionReveal} id="problem" className="relative scroll-mt-28 px-4 py-16 md:py-20">
            <div className="mx-auto max-w-7xl">
                <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.65, ease: 'easeOut' }}
                            className="max-w-2xl"
                        >
                            <Pill>Täglicher Überblick</Pill>
                            <h2 className="mt-6 text-4xl font-semibold leading-[1.08] tracking-[-0.05em] text-slate-950 sm:text-5xl">
                                Morgens sehen, was heute Profit kostet.
                            </h2>
                            <p className="mt-5 max-w-xl text-base leading-8 text-slate-500">
                                commetriq zeigt die wichtigsten Signale aus Umsatz, Marge, Bestand und Retouren, bevor du in Listen abtauchst.
                            </p>
                        </motion.div>

                        <div className="mt-8 max-w-2xl border-y border-slate-200">
                            {questions.map(({ icon: Icon, eyebrow, title, metric, hint }, index) => (
                                <motion.div
                                    key={title}
                                    initial={{ opacity: 0, x: -18 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1, duration: 0.55, ease: 'easeOut' }}
                                    className="grid grid-cols-[42px_1fr_auto] gap-4 border-b border-slate-200 py-5 last:border-b-0"
                                >
                                    <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                                        <Icon className="h-5 w-5" strokeWidth={2.8} />
                                    </span>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{eyebrow}</p>
                                        <p className="mt-1 text-base font-semibold tracking-tight text-slate-950">{title}</p>
                                        <p className="mt-1 text-sm leading-6 text-slate-500">{hint}</p>
                                    </div>
                                    <p className="hidden text-right text-lg font-semibold tracking-tight text-slate-950 sm:block">{metric}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="relative">
                        <motion.div
                            initial={{ opacity: 0, y: 22, scale: 0.99 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.65, ease: 'easeOut' }}
                            className="grid gap-3 md:grid-cols-4"
                        >
                            <div className="overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white shadow-[0_16px_42px_rgba(15,23,42,0.06)] md:col-span-4">
                                <div className="grid md:grid-cols-[0.9fr_1fr]">
                                    <div className="relative h-44 overflow-hidden border-b border-slate-200 bg-slate-50 md:border-b-0 md:border-r">
                                        <img
                                            src="/versand-performance-screenshot.png"
                                            alt="Aktuelle commetriq Ansicht"
                                            className="h-full w-full object-cover object-left-top"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-center justify-between">
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-orange-600">Heute im Board</p>
                                            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">aktualisiert</span>
                                        </div>
                                        <div className="mt-5 grid grid-cols-3 gap-3">
                                            {[
                                                ['Bestellungen', '1505'],
                                                ['Rohertrag', '31,8%'],
                                                ['Retouren', '4,2%'],
                                            ].map(([label, value]) => (
                                                <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
                                                    <p className="mt-2 text-lg font-semibold tracking-tight text-slate-950">{value}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-[1.15rem] border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.05)] md:col-span-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-slate-950">Low Stock</p>
                                    <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">12 SKUs</span>
                                </div>
                                <div className="mt-4 space-y-2">
                                    {[
                                        ['Comfort UP Pumpe', 'Bestand 7'],
                                        ['KAMAN Dichtungsband', 'Bestand 4'],
                                    ].map(([name, stock]) => (
                                        <div key={name} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                                            <span className="truncate text-xs font-semibold text-slate-700">{name}</span>
                                            <span className="shrink-0 text-xs text-red-500">{stock}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-[1.15rem] border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.05)] md:col-span-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-slate-950">Kanal-Marge</p>
                                    <span className="text-xs font-semibold text-emerald-600">Shop +4,6%</span>
                                </div>
                                <div className="mt-4 space-y-3">
                                    {[
                                        ['Amazon', '/amazon.png', 78],
                                        ['eBay', '/ebay.png', 48],
                                        ['Shop', null, 66],
                                    ].map(([label, src, width]) => (
                                        <div key={label} className="grid grid-cols-[54px_1fr] items-center gap-3">
                                            <span className="flex h-8 items-center justify-center rounded-lg bg-slate-50 text-xs font-semibold text-slate-600">
                                                {src ? <img src={src} alt={label} className="max-h-4 max-w-10 object-contain" loading="lazy" /> : label}
                                            </span>
                                            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    whileInView={{ width: `${width}%` }}
                                                    viewport={{ once: true }}
                                                    transition={{ duration: 0.7, ease: 'easeOut' }}
                                                    className="h-full rounded-full bg-orange-500"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-[1.15rem] border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.05)] md:col-span-2">
                                <p className="text-sm font-semibold text-slate-950">Retouren-Signal</p>
                                <div className="mt-4 rounded-xl bg-orange-50 px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-orange-600">Top Grund</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-950">Falscher Artikel bestellt</p>
                                </div>
                                <div className="mt-3 flex items-center justify-between text-sm">
                                    <span className="text-slate-500">Quote</span>
                                    <span className="font-semibold text-slate-950">4,2%</span>
                                </div>
                            </div>

                            <div className="rounded-[1.15rem] border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.05)] md:col-span-2">
                                <p className="text-sm font-semibold text-slate-950">Profit-Produkte</p>
                                <div className="mt-4 space-y-2">
                                    {['3.810 EUR', '1.630 EUR', '1.537 EUR'].map((value, index) => (
                                        <div key={value} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0 last:pb-0">
                                            <span className="text-xs font-semibold text-slate-500">#{index + 1}</span>
                                            <span className="text-sm font-semibold text-slate-950">{value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};

const BeforeAfterSection = () => {
    const before = [
        'CSV-Exporte aus Wawi, Shop und Marktplätzen zusammensuchen',
        'Marge, Retouren und Lager in getrennten Tabellen prüfen',
        'Entscheidungen nach Bauchgefühl oder verspäteten Reports treffen',
    ];
    const after = [
        'Umsatz, Rohertrag, Bestand und Retouren in einer Ansicht sehen',
        'Auffällige Produkte und Kanäle direkt nach Priorität erkennen',
        'Jeden Morgen mit klaren Aufgaben statt offenen Listen starten',
    ];

    return (
        <motion.section {...sectionReveal} className="relative overflow-hidden border-y border-slate-200 bg-slate-50 px-4 py-16 md:py-20">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_22%_12%,rgba(229,92,26,0.075),transparent_28%),linear-gradient(180deg,#f8fafc_0%,#ffffff_100%)]" />
            <div className="relative mx-auto grid max-w-7xl gap-9 lg:grid-cols-[0.35fr_0.65fr] lg:items-center">
                <div className="max-w-xl">
                    <p className="text-sm font-semibold uppercase tracking-[0.3em] text-orange-600">Vorher / Nachher</p>
                    <h2 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-[-0.055em] text-slate-950 md:text-5xl">
                        Aus verstreuten Listen wird ein klarer Arbeitsstart.
                    </h2>
                    <p className="mt-5 text-base leading-8 text-slate-600">
                        commetriq ersetzt nicht Ihr Tagesgeschäft. Es sortiert es, bevor Sie den ersten Export öffnen.
                    </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    <motion.article
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.56, ease: 'easeOut' }}
                        className="rounded-[1.1rem] border border-slate-200 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)]"
                    >
                        <div className="flex items-center justify-between gap-4">
                            <h3 className="text-2xl font-semibold tracking-[-0.04em] text-slate-950">Heute oft</h3>
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                                <X className="h-5 w-5" strokeWidth={2.4} />
                            </span>
                        </div>
                        <div className="mt-6 space-y-4">
                            {before.map((item) => (
                                <div key={item} className="flex gap-3 text-sm leading-6 text-slate-600">
                                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-slate-300" />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </motion.article>

                    <motion.article
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.56, delay: 0.08, ease: 'easeOut' }}
                        className="rounded-[1.1rem] border border-slate-950 bg-slate-950 p-6 text-white shadow-[0_24px_70px_rgba(15,23,42,0.2)]"
                    >
                        <div className="flex items-center justify-between gap-4">
                            <h3 className="text-2xl font-semibold tracking-[-0.04em] text-white">Mit commetriq</h3>
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500 text-white">
                                <Check className="h-5 w-5" strokeWidth={2.8} />
                            </span>
                        </div>
                        <div className="mt-6 space-y-4">
                            {after.map((item) => (
                                <div key={item} className="flex gap-3 text-sm leading-6 text-slate-200">
                                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-orange-300/60 text-orange-300">
                                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                                    </span>
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </motion.article>
                </div>
            </div>
        </motion.section>
    );
};

const VisualBeforeAfterSection = () => {
    const beforePoints = [
        'Manuelle Exporte aus vielen Quellen',
        'Unvollständige Daten und doppelte Arbeit',
        'Keine Priorisierung, was wirklich zählt',
        'Fehleranfällige Formeln und Versionschaos',
        'Entscheidungen auf Basis veralteter Zahlen',
    ];
    const afterPoints = [
        'Alle Daten automatisch zusammengeführt',
        'Bewertet und sortiert nach Relevanz',
        'Tagespriorität auf einen Blick',
        'Kennzahlen jederzeit aktuell und vergleichbar',
        'Klare nächste Schritte für dein Tagesgeschäft',
    ];
    return (
        <motion.section {...sectionReveal} id="before-after" className="relative scroll-mt-4 overflow-hidden border-y border-slate-200 bg-white">
            <div className="relative w-full overflow-hidden bg-white">
                <div className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-px bg-slate-200 xl:block" />
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-30 hidden h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-orange-200 bg-white text-orange-600 shadow-[0_18px_46px_rgba(229,92,26,0.18)] xl:flex">
                    <ArrowRight className="h-8 w-8" strokeWidth={2.2} />
                </div>

                <div className="grid xl:grid-cols-2">
                    <div className="commetriq-before-after-panel relative overflow-hidden bg-white px-7 pb-14 pt-24 sm:px-10 lg:px-10 lg:py-20 xl:px-14">
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_62%_18%,rgba(15,23,42,0.05),transparent_32%),linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)]" />
                        <p className="absolute left-7 top-8 z-20 text-2xl font-black uppercase tracking-[0.24em] text-slate-300 sm:left-10 lg:left-12 xl:left-16 xl:text-3xl">Vorher</p>

                        <div className="commetriq-before-after-grid commetriq-before-grid relative z-10 grid gap-10 lg:min-h-[520px] lg:items-center">
                            <div className="max-w-[350px]">
                                <h2 className="text-3xl font-semibold leading-[1.08] tracking-[-0.045em] text-slate-950 xl:text-4xl">
                                    Daten überall.<br />Überblick nirgends.
                                </h2>
                                <div className="mt-8 grid gap-3.5">
                                    {beforePoints.map(point => (
                                        <div key={point} className="flex items-center gap-3.5 text-sm font-medium leading-6 text-slate-500 xl:text-base">
                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-400 text-white">
                                                <X className="h-4 w-4" strokeWidth={3} />
                                            </span>
                                            <span>{point}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="commetriq-before-visual relative mx-auto aspect-square w-full max-w-[520px]">
                                <motion.img
                                    src="/before-spreadsheet-files.png"
                                    alt="Manuelle Tabelle mit schwebenden Dateien für Bestellungen, Retouren, Lagerbestand, Werbung und Kosten"
                                    initial={{ opacity: 0, y: 20, scale: 0.96 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    viewport={{ once: true, amount: 0.35 }}
                                    transition={{ delay: 0.35, duration: 0.8, ease: 'easeOut' }}
                                    className="absolute inset-0 h-full w-full object-contain"
                                    loading="lazy"
                                    decoding="async"
                                    width="1254"
                                    height="1254"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="commetriq-before-after-panel relative overflow-hidden border-t border-orange-200/70 bg-[#fff1e6] px-7 pb-14 pt-24 sm:px-10 lg:px-10 lg:py-20 xl:border-t-0 xl:px-14">
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(229,92,26,0.22),transparent_36%),linear-gradient(90deg,#fff1e6_0%,#fff8f1_68%,#ffffff_100%)]" />
                        <p className="absolute right-7 top-8 z-20 text-right text-2xl font-black uppercase tracking-[0.24em] text-orange-600 sm:right-10 lg:right-12 xl:right-16 xl:text-3xl">Nachher</p>

                        <div className="commetriq-before-after-grid commetriq-after-grid relative z-10 grid gap-10 lg:min-h-[560px] lg:items-center">
                            <div className="max-w-[360px]">
                                <h2 className="text-3xl font-semibold leading-[1.08] tracking-[-0.045em] text-slate-950 xl:text-4xl">
                                    Alles gebündelt.<br />Das Wesentliche zuerst.
                                </h2>
                                <div className="mt-8 grid gap-3.5">
                                    {afterPoints.map(point => (
                                        <div key={point} className="flex items-center gap-3.5 text-sm font-medium leading-6 text-slate-500 xl:text-base">
                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-orange-600 shadow-sm">
                                                <Check className="h-4 w-4" strokeWidth={3.2} />
                                            </span>
                                            <span>{point}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="commetriq-after-visual relative flex min-h-[350px] items-center justify-center lg:min-h-[410px]">
                                <OptimizedImage
                                    src="/nachher-tablet-dashboard-transparent.png"
                                    alt="commetriq Dashboard auf einem Tablet mit Umsatz, Bestellungen und Profit-Kennzahlen"
                                    className="h-auto w-[108%] max-w-[590px] select-none rounded-[1.4rem] drop-shadow-[0_34px_82px_rgba(15,23,42,0.22)]"
                                    loading="lazy"
                                    decoding="async"
                                    compactWidth={720}
                                    sizes="(min-width: 1536px) 720px, (min-width: 1024px) 50vw, 100vw"
                                    width="1448"
                                    height="1086"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};
const SolutionsPlatformSection = () => {
    const solutionFilters = [
        [Database, 'Alle Bereiche', 'Der gesamte Arbeitsüberblick', '/solution-dashboard.png', 'Dashboard mit allen zentralen Shop-Kennzahlen', 1809, 1036],
        [BarChart3, 'Übersicht', 'Alle KPIs auf einen Blick', '/solution-dashboard.png', 'Dashboard-Übersicht mit Umsatz, Gewinn, Marge, Lager und Retouren', 1809, 1036],
        [LineChart, 'Profit', 'Rohertrag und Marge verstehen', '/solution-profit.png', 'Gewinnanalyse mit Profitverlauf, Kosten und Artikelgruppen', 1801, 1037],
        [Boxes, 'Lager', 'Bestände rechtzeitig steuern', '/solution-inventory.png', 'Lager- und Bestandsanalyse mit Kapitalbindung und Risikoverteilung', 1802, 976],
        [RefreshCw, 'Retouren', 'Quoten und Ursachen erkennen', '/solution-returns.png', 'Retourenanalyse mit Retourenquote, Gründen und Risikoartikeln', 1806, 1038],
        [Sparkles, 'Kosten', 'Gebühren und Fixkosten prüfen', '/solution-costs.png', 'Kostenübersicht mit Zahlungsgebühren, Kostenliste und Kategorie-Mix', 1805, 1029],
        [FileSpreadsheet, 'Reports', 'Kennzahlen strukturiert auswerten', '/solution-reports.png', 'Report-Ansicht mit Deckungsbeiträgen nach Verkaufskanal', 1809, 1022],
    ];
    const [activeSolution, setActiveSolution] = useState('Alle Bereiche');
    const selectedSolution = solutionFilters.find(([, label]) => label === activeSolution) || solutionFilters[0];

    return (
        <motion.section {...sectionReveal} id="solutions" className="commetriq-deferred-section commetriq-deferred-section-large relative scroll-mt-28 overflow-hidden border-y border-orange-100/70 bg-white px-4 py-20 md:py-24 lg:px-10 lg:py-20 xl:px-12 2xl:py-28">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(229,92,26,0.055),transparent_30%),linear-gradient(180deg,#ffffff_0%,#fffdf9_100%)]" />
            <div
                className="pointer-events-none absolute right-6 top-8 hidden h-[360px] w-[360px] opacity-[0.26] lg:block"
                style={{
                    backgroundImage: 'radial-gradient(rgba(229,92,26,0.18) 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                }}
            />

            <div className="relative mx-auto max-w-[1540px]">
                <div className="mx-auto max-w-4xl text-center">
                    <p className="text-sm font-semibold uppercase tracking-[0.42em] text-orange-600">Lösungen</p>
                    <h2 className="mt-4 text-4xl font-semibold leading-[1.04] tracking-[-0.055em] text-slate-950 md:text-5xl 2xl:text-6xl">
                        Ein Board. Alle entscheidenden Bereiche.
                    </h2>
                    <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-slate-600 xl:text-lg xl:leading-8">
                        Von Profit und Lager bis Retouren und Kunden: commetriq bündelt die entscheidenden Bereiche deines Shops in einer klaren Arbeitsoberfläche.
                    </p>
                </div>

                <div className="mx-auto mt-10 grid max-w-[1440px] items-center gap-8 xl:mt-12 xl:grid-cols-[230px_minmax(0,1fr)] 2xl:gap-12">
                    <aside className="min-w-0">
                        <div className="mb-5 hidden xl:block">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Arbeitsbereiche</p>
                            <p className="mt-2 text-sm leading-6 text-slate-500">Wähle den Bereich, den du im Dashboard prüfen möchtest.</p>
                        </div>
                        <div
                            className="flex gap-2 overflow-x-auto pb-3 xl:grid xl:overflow-visible xl:pb-0"
                            role="tablist"
                            aria-label="commetriq Arbeitsbereiche"
                            aria-orientation="vertical"
                        >
                            {solutionFilters.map(([Icon, label, description]) => {
                                const active = activeSolution === label;
                                return (
                                    <motion.button
                                        key={label}
                                        type="button"
                                        onClick={() => setActiveSolution(label)}
                                        whileHover={{ x: 3 }}
                                        transition={{ duration: 0.18 }}
                                        className={cx(
                                            'group flex min-w-max items-center gap-3 rounded-lg border px-3 py-3 text-left transition-all focus:outline-none focus:ring-4 focus:ring-orange-100 xl:min-w-0',
                                            active
                                                ? 'border-orange-200 bg-orange-50 text-slate-950 shadow-[0_10px_28px_rgba(229,92,26,0.09)]'
                                                : 'border-transparent bg-transparent text-slate-600 hover:border-slate-200 hover:bg-white'
                                        )}
                                        role="tab"
                                        aria-selected={active}
                                    >
                                        <span className={cx(
                                            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors',
                                            active ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-orange-50 group-hover:text-orange-600'
                                        )}>
                                            <Icon className="h-5 w-5" strokeWidth={2.1} />
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-sm font-semibold">{label}</span>
                                            <span className="mt-0.5 hidden text-xs font-medium leading-4 text-slate-400 xl:block">{description}</span>
                                        </span>
                                        <ChevronRight className={cx('ml-auto hidden h-4 w-4 shrink-0 xl:block', active ? 'text-orange-600' : 'text-slate-300')} />
                                    </motion.button>
                                );
                            })}
                        </div>
                    </aside>

                    <motion.div
                        initial={{ opacity: 0, y: 28, scale: 0.985 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true, margin: '0px 0px -10% 0px', amount: 0.12 }}
                        transition={{ duration: 0.72, ease: 'easeOut' }}
                        className="relative min-w-0 pb-16 sm:pb-20 xl:w-full xl:justify-self-center 2xl:max-w-[1100px]"
                    >
                        <div className="relative z-10 aspect-video overflow-hidden rounded-[1.2rem] border-[8px] border-slate-900 bg-white shadow-[0_32px_72px_rgba(15,23,42,0.16)] sm:border-[10px]">
                            <span className="absolute left-1/2 top-[-7px] z-20 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-slate-600 ring-2 ring-slate-800 sm:top-[-8px]" />
                            <AnimatePresence initial={false}>
                                <motion.div
                                    key={selectedSolution[1]}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                                    className="absolute inset-0"
                                >
                                    <OptimizedImage
                                        src={selectedSolution[3]}
                                        alt={selectedSolution[4]}
                                        className="h-full w-full select-none rounded-[0.65rem] bg-white object-contain"
                                        compactWidth={960}
                                        sizes="(min-width: 1024px) 960px, 100vw"
                                        loading="lazy"
                                        decoding="async"
                                        width={selectedSolution[5]}
                                        height={selectedSolution[6]}
                                    />
                                </motion.div>
                            </AnimatePresence>
                        </div>
                        <div className="absolute bottom-6 left-1/2 z-0 h-16 w-[13%] min-w-[76px] -translate-x-1/2 bg-gradient-to-b from-slate-700 via-slate-500 to-slate-300 [clip-path:polygon(28%_0,72%_0,88%_100%,12%_100%)] sm:h-20" />
                        <div className="absolute bottom-3.5 left-1/2 z-10 h-3 w-[38%] -translate-x-1/2 rounded-[50%] bg-slate-700 shadow-[0_12px_22px_rgba(15,23,42,0.2)] sm:h-3.5" />
                        <div className="absolute bottom-2.5 left-1/2 z-0 h-2.5 w-[44%] -translate-x-1/2 rounded-[50%] bg-slate-300/70 blur-sm" />
                    </motion.div>
                </div>
            </div>
        </motion.section>
    );
};
const SecureStartSection = () => {
    const safeguards = [
        [ShieldCheck, 'Kein direkter Serverzugriff'],
        [Lock, 'Verschlüsselte Übertragung'],
        ['germany', 'Gehostet in Deutschland'],
    ];
    const steps = [
        {
            number: '01',
            title: 'Daten übertragen',
            text: 'JTL-Wawi, CSV, Excel, E-Mail oder SFTP werden sicher an commetriq übergeben, ohne direkten Zugriff auf deinen Server.',
            image: '/commetriq_01_daten_uebertragen_ohne_rand_hq.webp',
            alt: 'Schritt 1: Daten aus JTL-Wawi, CSV, Excel, E-Mail und SFTP sicher an commetriq übertragen',
            points: ['JTL-Wawi, CSV und Excel anbinden', 'E-Mail und SFTP sicher übertragen', 'Kein direkter Zugriff auf deinen Server'],
        },
        {
            number: '02',
            title: 'Geschützt verarbeiten',
            text: 'Deine Daten werden verschlüsselt verarbeitet und in Deutschland geschützt für deine Auswertungen vorbereitet.',
            image: '/commetriq_02_geschuetzt_verarbeiten_ohne_rand_hq.webp',
            alt: 'Schritt 2: Daten TLS-verschlüsselt und DSGVO-konform in Deutschland verarbeiten',
            points: ['TLS-verschlüsselte Übertragung', 'DSGVO-konforme Verarbeitung', 'Hosting in Deutschland'],
        },
        {
            number: '03',
            title: 'Direkt nutzen',
            text: 'Kennzahlen, Trends, Alerts und Aufträge stehen bereit, damit du Erkenntnisse sofort in bessere Entscheidungen übersetzt.',
            image: '/commetriq_03_direkt_nutzen_ohne_rand_hq.webp',
            alt: 'Schritt 3: commetriq Dashboard mit Kennzahlen, Trends, Alerts und Aufträgen direkt nutzen',
            points: ['Kennzahlen und Trends sofort nutzen', 'Alerts und Prioritäten erkennen', 'Aufträge gezielt weiterbearbeiten'],
        },
    ];

    return (
        <section id="ablauf" className="commetriq-deferred-section commetriq-deferred-section-tall relative scroll-mt-24 bg-slate-50 px-4 py-20 md:py-24 lg:px-10 xl:px-12 xl:py-28">
            <div className="relative mx-auto max-w-[1320px] lg:grid lg:grid-cols-[0.78fr_1.22fr] lg:items-start lg:gap-12 xl:gap-16">
                <div className="mx-auto max-w-4xl text-center lg:sticky lg:top-28 lg:self-start lg:max-w-none lg:text-left xl:top-32">
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">So funktioniert es</p>
                    <h2 className="mt-4 text-4xl font-semibold leading-[1.06] tracking-[-0.055em] text-slate-950 md:text-5xl lg:text-[2.75rem] xl:text-5xl">
                        So startet commetriq sicher und einfach.
                    </h2>
                    <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-600 md:text-lg md:leading-8 lg:mx-0">
                        Deine Daten kommen sicher aus deinen Systemen in commetriq und werden direkt zu einem klaren Arbeitsüberblick.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-2.5 lg:justify-start">
                        {safeguards.map(([Icon, label]) => (
                            <span key={label} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 shadow-sm">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                                    {Icon === 'germany' ? (
                                        <span className="block h-4 w-4 overflow-hidden rounded-full border border-white shadow-sm">
                                            <span className="block h-1/3 bg-slate-950" />
                                            <span className="block h-1/3 bg-red-600" />
                                            <span className="block h-1/3 bg-yellow-400" />
                                        </span>
                                    ) : (
                                        <Icon className="h-3.5 w-3.5" strokeWidth={2.3} />
                                    )}
                                </span>
                                {label}
                            </span>
                        ))}
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-semibold text-slate-500 lg:justify-start">
                        <a href="/datenschutz" className="underline underline-offset-4 hover:text-slate-950">Datenschutz ansehen</a>
                        <a href="/avv" className="underline underline-offset-4 hover:text-slate-950">AVV ansehen</a>
                    </div>
                </div>
                <div className="mt-14 grid gap-0 lg:mt-0 lg:gap-y-24 lg:pb-8">
                    {steps.map((step, index) => {
                        const imageFirst = index % 2 === 1;
                        return (
                            <React.Fragment key={step.title}>
                                <motion.article
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.16 }}
                                transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
                                style={{ top: `calc(7rem + ${index * 1.25}rem)`, zIndex: index + 1 }}
                                className={cx('relative min-h-[460px] overflow-hidden rounded-lg border border-slate-200 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.08)] sm:p-8 lg:sticky lg:h-[540px] lg:self-start', index === steps.length - 1 && 'lg:mt-24')}
                            >
                                <div className="grid h-full gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
                                    <div className={cx('flex h-full flex-col justify-center', imageFirst ? 'lg:order-2' : '')}>
                                        <p className="text-sm font-semibold text-orange-600">{step.number}</p>
                                        <h3 className="mt-5 text-4xl font-semibold leading-tight tracking-[-0.055em] text-slate-950 xl:text-5xl">
                                            {step.title}
                                        </h3>
                                        <p className="mt-5 max-w-xl text-base leading-7 text-slate-600 xl:text-lg xl:leading-8">
                                            {step.text}
                                        </p>
                                        <div className="mt-8 space-y-3">
                                            {step.points.map(point => (
                                                <div key={point} className="flex items-center gap-3 text-sm font-medium text-slate-700">
                                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-600 text-white">
                                                        <Check className="h-3 w-3" strokeWidth={3} />
                                                    </span>
                                                    <span>{point}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div
                                        className={cx('relative isolate flex h-[320px] items-center justify-center overflow-hidden rounded-xl border border-orange-100/70 p-3 sm:h-[360px] sm:p-4 lg:h-full', imageFirst ? 'lg:order-1' : '')}
                                        style={{ background: 'radial-gradient(circle at 82% 18%, rgba(229,92,26,0.10), transparent 44%), linear-gradient(145deg, #fff9f3 0%, #fffdfb 58%, #f6f9fc 100%)' }}
                                    >
                                        <svg aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 400 500" preserveAspectRatio="none" fill="none">
                                            <path d="M-30 120 C80 25 240 50 430 -20" stroke="#E55C1A" strokeWidth="1.5" opacity="0.22" />
                                            <path d="M-25 139 C88 44 252 69 432 1" stroke="#E55C1A" strokeWidth="1" opacity="0.12" />
                                            <path d="M-30 510 C95 393 265 457 430 337" stroke="#102033" strokeWidth="1.5" opacity="0.10" />
                                        </svg>
                                        <img
                                            src={step.image}
                                            alt={step.alt}
                                            style={{ objectPosition: imageFirst ? '20% center' : 'center' }}
                                            className="relative z-10 h-[84%] w-full select-none rounded-[10px] border border-white/90 object-cover shadow-[0_12px_28px_rgba(15,23,42,0.10)] sm:h-[80%]"
                                            loading="lazy"
                                            decoding="async"
                                            width="2372"
                                            height="2544"
                                        />
                                    </div>
                                </div>
                            </motion.article>
                            {index < steps.length - 1 && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10, scale: 0.92 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    viewport={{ once: true, amount: 0.55 }}
                                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                                    className="flex h-16 items-center justify-center lg:hidden"
                                    aria-hidden="true"
                                >
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-orange-200 bg-orange-50 text-orange-600 shadow-[0_10px_24px_rgba(229,92,26,0.12)]">
                                        <ArrowDown className="h-4 w-4" strokeWidth={2.5} />
                                    </span>
                                </motion.div>
                            )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
const UseCasesSection = () => {
    const steps = [
        {
            number: '01',
            icon: Sun,
            title: 'Täglich prüfen',
            question: 'Was ist heute kritisch?',
            text: 'Low Stock, Bestellungen und Warnungen sofort sehen.',
            image: '/newsteps-morning.png',
            alt: 'Täglich prüfen mit Low Stock, offenen Bestellungen und Warnungen',
            width: 535,
        },
        {
            number: '02',
            icon: CalendarDays,
            title: 'Wöchentlich steuern',
            question: 'Welche Produkte und Kanäle bringen Ergebnis?',
            text: 'Marge, Rohertrag und Kanalvergleich im Blick behalten.',
            image: '/newsteps-weekly.png',
            alt: 'Wöchentlich steuern mit Rohertrag und Kanalvergleich',
            width: 530,
        },
        {
            number: '03',
            icon: FileSpreadsheet,
            title: 'Monatlich berichten',
            question: 'Welche Zahlen müssen exportiert oder geteilt werden?',
            text: 'Reports vorbereiten, Exporte nutzen und Entwicklungen nachvollziehen.',
            image: '/newsteps-monthly.png',
            alt: 'Monatlich berichten mit Monatsreport und Exportoptionen',
            width: 530,
        },
    ];

    return (
        <motion.section {...sectionReveal} id="use-cases" className="commetriq-deferred-section relative scroll-mt-28 overflow-hidden border-t border-slate-200 bg-white px-4 py-16 md:py-20 lg:px-10 lg:py-16 xl:px-12 xl:py-20">
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    backgroundImage: 'linear-gradient(90deg, rgba(15,23,42,0.035) 1px, transparent 1px), linear-gradient(180deg, rgba(15,23,42,0.03) 1px, transparent 1px), radial-gradient(circle at 50% 3%, rgba(229,92,26,0.055), transparent 34%)',
                    backgroundSize: '72px 72px, 72px 72px, auto',
                }}
            />
            <div
                className="pointer-events-none absolute -bottom-20 -left-16 h-[280px] w-[280px] opacity-[0.22]"
                style={{
                    backgroundImage: 'radial-gradient(rgba(229,92,26,0.32) 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                }}
            />
            <div className="pointer-events-none absolute -right-28 top-[-10rem] h-[420px] w-[420px] rounded-full border border-orange-200/60" />
            <div className="pointer-events-none absolute -right-16 top-[-6rem] h-[320px] w-[320px] rounded-full border border-orange-100/80" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-gradient-to-b from-transparent to-white" />

            <div className="relative mx-auto max-w-[1460px]">
                <div className="mx-auto max-w-4xl text-center">
                    <p className="text-sm font-semibold uppercase tracking-[0.26em] text-orange-600">Im Alltag</p>
                    <h2 className="mt-4 text-4xl font-semibold leading-[1.08] tracking-[-0.055em] text-slate-950 md:text-5xl 2xl:text-6xl">
                        Vom täglichen Check bis zum Monatsreport.
                    </h2>
                    <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-600 md:text-lg md:leading-8">
                        commetriq begleitet deine Routinen mit den passenden Antworten: morgens priorisieren, wöchentlich steuern und monatlich sauber berichten.
                    </p>
                </div>

                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                    variants={{
                        hidden: {},
                        show: { transition: { staggerChildren: 0.14, delayChildren: 0.1 } },
                    }}
                    className="mt-10 grid gap-8 lg:grid-cols-3 xl:gap-10 2xl:gap-16"
                >
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        return (
                        <motion.div
                            key={step.title}
                            variants={{
                                hidden: { opacity: 0, y: 30 },
                                show: { opacity: 1, y: 0 },
                            }}
                            transition={{ duration: 0.58, ease: 'easeOut' }}
                            className="relative flex flex-col items-center text-center"
                        >
                            {index < steps.length - 1 && (
                                <div className="absolute left-[60%] top-8 hidden h-px w-[78%] overflow-visible bg-slate-200 lg:block">
                                    <motion.span
                                        className="absolute left-0 top-0 h-px w-full origin-left bg-orange-500"
                                        initial={{ scaleX: 0 }}
                                        whileInView={{ scaleX: 1 }}
                                        viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                                        transition={{ delay: 0.34 + index * 0.24, duration: 0.62, ease: 'easeOut' }}
                                    />
                                    <motion.span
                                        className="absolute -right-1.5 -top-[4.5px] h-2.5 w-2.5 rotate-45 border-r border-t"
                                        initial={{ borderColor: '#cbd5e1', opacity: 0.6 }}
                                        whileInView={{ borderColor: BRAND, opacity: 1 }}
                                        viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                                        transition={{ delay: 0.88 + index * 0.24, duration: 0.2, ease: 'easeOut' }}
                                    />
                                </div>
                            )}
                            <div className="relative flex justify-center">
                                <motion.span
                                    className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white text-orange-600 shadow-[0_16px_42px_rgba(15,23,42,0.1)]"
                                    initial={{ color: BRAND, borderColor: '#e2e8f0' }}
                                    whileInView={{ color: '#ffffff', borderColor: BRAND }}
                                    viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                                    transition={{ delay: 0.18 + index * 0.24, duration: 0.2, ease: 'easeOut' }}
                                >
                                    <motion.span
                                        className="absolute inset-0 rounded-full bg-orange-600"
                                        initial={{ scale: 0 }}
                                        whileInView={{ scale: 1 }}
                                        viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                                        transition={{ delay: 0.12 + index * 0.24, duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
                                    />
                                    <Icon className="relative z-10 h-8 w-8" strokeWidth={2} />
                                </motion.span>
                            </div>

                            <div className="mt-7 min-h-[116px] max-w-[390px] text-center">
                                <div className="flex items-baseline justify-center gap-4">
                                    <span className="text-xl font-semibold text-orange-600">{step.number}</span>
                                    <h3 className="text-2xl font-semibold tracking-[-0.04em] text-slate-950">{step.title}</h3>
                                </div>
                                <p className="mt-4 text-base font-medium text-slate-950">{step.question}</p>
                                <p className="mt-2 text-base leading-6 text-slate-600">{step.text}</p>
                            </div>

                            <div className="mt-4 flex justify-center">
                                <OptimizedImage
                                    src={step.image}
                                    alt={step.alt}
                                    className="h-auto w-full max-w-[290px] select-none drop-shadow-[0_22px_58px_rgba(15,23,42,0.1)] md:max-w-[305px] xl:max-w-[315px] 2xl:max-w-[350px]"
                                    loading="lazy"
                                    decoding="async"
                                    sizes="(min-width: 1536px) 350px, (min-width: 1280px) 315px, 290px"
                                    width={step.width}
                                    height="585"
                                />
                            </div>
                        </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </motion.section>
    );
};

const AudienceSection = () => {
    const [openAudience, setOpenAudience] = useState(null);
    const audiences = [
        {
            title: 'Eigener Onlineshop',
            text: 'Für D2C-Marken und Shopbetreiber: Sieh Shop-Umsatz, Retouren und Wareneinsatz zusammen mit Produktmargen und Lagerbestand.',
        },
        {
            title: 'Verkauf über Amazon',
            text: 'Ordne Verkäufe, Wareneinsatz und gebuchte Amazon-Gebühren ein. So kannst du Provisionen und Fulfillmentkosten im Deckungsbeitrag nachvollziehen.',
        },
        {
            title: 'Verkauf über eBay',
            text: 'Prüfe eBay als eigenen Verkaufskanal. Welche Gebühren und Retouren aktuell verfügbar sind, klären wir für deine konkrete Anbindung.',
        },
        {
            title: 'Mehrere Verkaufskanäle',
            text: 'Stelle Shop, Amazon und eBay gegenüber. Welche Kostenwerte für den Kanalvergleich verfügbar und aktuell sind, klären wir vor dem Test.',
        },
        {
            title: 'Breites Sortiment und Lager',
            text: 'Finde Artikel mit knappem Bestand, langsamer Bewegung oder hoher Kapitalbindung und priorisiere, was du genauer prüfen solltest.',
        },
    ];

    return (
        <motion.section
            id="fuer-wen"
            className="commetriq-deferred-section relative scroll-mt-28 overflow-hidden px-4 py-20 sm:px-6 md:py-24 lg:px-10 lg:py-28 xl:px-12"
            style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fb 14%, #edf0f5 50%, #f8f9fb 86%, #ffffff 100%)' }}
        >
            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.08 } } }}
                className="mx-auto grid max-w-[1340px] gap-x-12 gap-y-10 lg:grid-cols-[minmax(0,1.48fr)_minmax(370px,1fr)] lg:gap-y-12 xl:gap-x-16"
            >
                <motion.div
                    variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0 } }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-[760px]"
                >
                    <h2 className="text-4xl font-semibold leading-[1.09] tracking-[-0.055em] text-slate-950 sm:text-5xl xl:text-[3.4rem]">
                        Für welche <span className="text-orange-600">Händler</span> eignet sich commetriq?
                    </h2>
                    <p className="mt-6 max-w-[720px] text-lg leading-8 text-slate-600 xl:text-[1.35rem] xl:leading-9">
                        Ob eigener Shop, Amazon, eBay oder mehrere Kanäle: Wenn JTL-Wawi deine Auftragsbasis ist, zeigt commetriq Umsatz und weitere verfügbare Kennzahlen in einer gemeinsamen Sicht. Den Datenumfang klären wir vor dem Test.
                    </p>
                </motion.div>

                <motion.div
                    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
                    className="flex flex-col gap-3.5 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center"
                >
                    {audiences.map(({ title, text }, index) => {
                        const isOpen = openAudience === index;
                        const panelId = `audience-panel-${index}`;
                        return (
                            <motion.div
                                key={title}
                                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                className="overflow-hidden rounded-2xl border border-[#dbe1ed] bg-white/85 shadow-[0_4px_20px_rgba(15,23,42,0.025)]"
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenAudience(isOpen ? null : index)}
                                    aria-expanded={isOpen}
                                    aria-controls={panelId}
                                    className="flex min-h-[74px] w-full items-center justify-between gap-5 px-6 text-left text-xl font-semibold text-slate-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-orange-600"
                                >
                                    <span>{title}</span>
                                    <Plus className={cx('h-6 w-6 shrink-0 stroke-[1.5] transition-transform', isOpen && 'rotate-45')} aria-hidden="true" />
                                </button>
                                <div id={panelId} hidden={!isOpen} className="border-t border-slate-100 px-6 pb-6 pt-4">
                                    <p className="text-base leading-7 text-slate-600">{text}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </motion.div>

                <motion.div
                    variants={{ hidden: { opacity: 0, y: 34, scale: 0.97 }, show: { opacity: 1, y: 0, scale: 1 } }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="relative mx-auto mt-1 flex w-full max-w-[800px] flex-col-reverse items-stretch gap-5 pb-2 sm:flex-row sm:items-center sm:gap-0 lg:col-start-1 lg:mt-0 lg:min-h-[350px]"
                >
                    <div className="relative z-10 w-full rounded-2xl border border-[#dbe1ed] bg-white px-5 py-5 shadow-[0_16px_38px_rgba(15,23,42,0.08)] sm:w-[40%] sm:shrink-0 sm:px-7 sm:py-6">
                        <span className="block h-10 text-[3.5rem] font-bold leading-none text-orange-600" aria-hidden="true">“</span>
                        <p className="mt-1 text-sm leading-6 text-slate-900 sm:text-base sm:leading-7">
                            Umsatz, Kosten und Lager an einem Ort. Sieh, welche Verkäufe tatsächlich zum Ergebnis beitragen.
                        </p>
                    </div>
                    <motion.img
                        src="/merchant-workspace.jpg"
                        alt="Händlerin arbeitet im eigenen Shop am Laptop zwischen Produkten und Versandkartons"
                        width="1586"
                        height="1013"
                        loading="lazy"
                        decoding="async"
                        initial={false}
                        animate={{ scale: 1, rotate: 2, y: 0 }}
                        whileHover={{ scale: 1.035, rotate: 0.5, y: -5 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                        className="relative z-20 w-full min-w-0 rounded-[1.35rem] border-[5px] border-white object-cover shadow-[0_24px_52px_rgba(15,23,42,0.14)] sm:-ml-4 sm:w-[62%]"
                    />
                </motion.div>
            </motion.div>
        </motion.section>
    );
};

const LegacyIntegrationsSection = () => {
    const integrationGraphicRef = useRef(null);
    const integrationGraphicInView = useInView(integrationGraphicRef, {
        amount: 0.35,
        margin: '-120px 0px -120px 0px',
    });
    const canvas = { width: 760, height: 420 };
    const bubbleSize = 64;
    const hubSize = 112;
    const bubbleRadius = bubbleSize / 2;
    const hubRadius = hubSize / 2;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const integrations = [
        { label: 'Amazon', logo: '/integration-logos/amazon.png', x: 76, y: 70 },
        { label: 'eBay', logo: '/integration-logos/ebay.png', x: 76, y: 210 },
        { label: 'Shopify', logo: '/integration-logos/shopify.png', x: 76, y: 350 },
        { label: 'DATEV', logo: '/integration-logos/datev.png', x: 684, y: 70 },
        { label: 'DHL', logo: '/integration-logos/dhl.png', x: 684, y: 210 },
        { label: 'PayPal', logo: '/integration-logos/paypal.png', x: 684, y: 350 },
    ];
    const connectorPaths = [
        `M${integrations[0].x + bubbleRadius} ${integrations[0].y} H196 Q218 ${integrations[0].y} 218 94 V130 Q218 154 242 154 H${centerX - hubRadius}`,
        `M${integrations[1].x + bubbleRadius} ${integrations[1].y} H${centerX - hubRadius}`,
        `M${integrations[2].x + bubbleRadius} ${integrations[2].y} H196 Q218 ${integrations[2].y} 218 326 V290 Q218 266 242 266 H${centerX - hubRadius}`,
        `M${integrations[3].x - bubbleRadius} ${integrations[3].y} H564 Q542 ${integrations[3].y} 542 94 V130 Q542 154 518 154 H${centerX + hubRadius}`,
        `M${integrations[4].x - bubbleRadius} ${integrations[4].y} H${centerX + hubRadius}`,
        `M${integrations[5].x - bubbleRadius} ${integrations[5].y} H564 Q542 ${integrations[5].y} 542 326 V290 Q542 266 518 266 H${centerX + hubRadius}`,
    ];
    const centerOnCanvas = (x, y, size) => ({
        left: `calc(${(x / canvas.width) * 100}% - ${size / 2}px)`,
        top: `calc(${(y / canvas.height) * 100}% - ${size / 2}px)`,
    });

    return (
        <motion.section {...sectionReveal} id="integrationen" className="relative scroll-mt-28 overflow-hidden border-y border-slate-200 bg-slate-50 px-4 py-24 md:py-28">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(229,92,26,0.08),transparent_30%),linear-gradient(180deg,#f8fafc_0%,#ffffff_52%,#f8fafc_100%)]" />
            <div
                className="pointer-events-none absolute -right-20 top-16 h-[360px] w-[360px] opacity-[0.18]"
                style={{
                    backgroundImage: 'radial-gradient(rgba(15,23,42,0.46) 1px, transparent 1px)',
                    backgroundSize: '18px 18px',
                }}
            />
            <div className="relative mx-auto grid max-w-[1460px] gap-12 lg:grid-cols-[0.34fr_0.66fr] lg:items-center">
                <div className="max-w-xl">
                    <p className="text-sm font-semibold uppercase tracking-[0.32em] text-orange-600">Integrationen</p>
                    <h2 className="mt-6 max-w-xl text-5xl font-semibold leading-[1.06] tracking-[-0.055em] text-slate-950 lg:text-6xl">
                        Womit arbeitet es zusammen?
                    </h2>
                    <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600">
                        commetriq verbindet Ihre wichtigsten Shop-, Marktplatz- und Exportdaten zu einem klaren Cockpit.
                    </p>
                    <div className="mt-10 grid max-w-lg grid-cols-2 gap-3">
                        {['Marktplätze', 'Payment', 'Versand', 'Export'].map((item) => (
                            <div key={item} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm">
                                {item}
                            </div>
                        ))}
                    </div>
                </div>

                <motion.div
                    ref={integrationGraphicRef}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                    variants={{
                        hidden: {},
                        show: { transition: { staggerChildren: 0.08, delayChildren: 0.08 } },
                    }}
                    className="relative min-h-[500px] lg:h-[420px] lg:min-h-0"
                >
                    <svg className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block" viewBox="0 0 760 420" fill="none" aria-hidden="true">
                        {connectorPaths.map((path) => (
                            <path
                                key={`${path}-base`}
                                d={path}
                                stroke="#cbd5e1"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeDasharray="5 8"
                                opacity="0.72"
                            />
                        ))}
                        {connectorPaths.map((path, index) => (
                            <motion.path
                                key={`${path}-active`}
                                d={path}
                                stroke={BRAND}
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeDasharray="5 8"
                                initial={{ opacity: 0, strokeDashoffset: 44 }}
                                animate={integrationGraphicInView
                                    ? { opacity: 0.98, strokeDashoffset: 0 }
                                    : { opacity: 0, strokeDashoffset: 44 }}
                                transition={{ delay: 0.16 + index * 0.08, duration: 0.9, ease: 'easeOut' }}
                            >
                                {integrationGraphicInView && (
                                    <animate
                                        attributeName="stroke-dashoffset"
                                        from="0"
                                        to="-13"
                                        dur="0.95s"
                                        begin={`${1.06 + index * 0.08}s`}
                                        repeatCount="indefinite"
                                    />
                                )}
                            </motion.path>
                        ))}
                    </svg>

                    <motion.div
                        className="absolute z-20"
                        style={centerOnCanvas(centerX, centerY, hubSize)}
                        variants={{
                            hidden: { opacity: 0, scale: 0.82 },
                            show: { opacity: 1, scale: 1 },
                        }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <motion.div
                            className="flex h-28 w-28 items-center justify-center rounded-[1.55rem] border border-orange-100 bg-white text-6xl font-black text-orange-600 shadow-[0_28px_70px_rgba(229,92,26,0.18)]"
                            animate={{ scale: [1, 1.025, 1] }}
                            transition={{ duration: 5.8, repeat: Infinity, ease: 'easeInOut' }}
                        >
                            W
                        </motion.div>
                    </motion.div>

                    {integrations.map((item, index) => (
                        <motion.div
                            key={item.label}
                            className="absolute z-10"
                            style={centerOnCanvas(item.x, item.y, bubbleSize)}
                            variants={{
                                hidden: { opacity: 0, scale: 0.72, y: 18 },
                                show: { opacity: 1, scale: 1, y: 0 },
                            }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <motion.div
                                className="flex h-16 w-16 items-center justify-center rounded-full border border-slate-200 bg-white shadow-[0_18px_48px_rgba(15,23,42,0.1)]"
                                animate={{ scale: [1, index % 2 ? 1.035 : 1.025, 1] }}
                                transition={{ duration: 5.4 + index * 0.22, repeat: Infinity, ease: 'easeInOut' }}
                            >
                                <img
                                    src={item.logo}
                                    alt={`${item.label} Logo`}
                                    className="h-11 w-12 object-contain"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </motion.div>
                        </motion.div>
                    ))}

                    <motion.a
                        href="#preise"
                        style={{ x: '-50%' }}
                        variants={{
                            hidden: { opacity: 0, y: 18 },
                            show: { opacity: 1, y: 0 },
                        }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        className="absolute bottom-4 left-1/2 z-20 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-950 shadow-[0_16px_42px_rgba(15,23,42,0.08)] transition-all hover:border-orange-200 hover:text-orange-600"
                    >
                        <span className="text-xl leading-none text-orange-600">+</span>
                        Pakete ansehen
                    </motion.a>
                </motion.div>
            </div>
        </motion.section>
    );
};

const IntegrationsSection = () => {
    const integrationFlowRef = useRef(null);
    const integrationFlowInView = useInView(integrationFlowRef, {
        amount: 0.34,
        margin: '0px 0px -70px 0px',
        once: true,
    });
    const integrations = [
        {
            name: 'JTL-Wawi',
            logo: '/integration-logos/jtl-wawi.png',
            text: 'Aufträge, Artikel, Lagerbestände und Retouren',
            side: 'left',
            top: 0,
            path: 'M328 52 H392 Q420 52 420 80 V122 Q420 150 448 150 H518',
        },
        {
            name: 'Amazon',
            logo: '/integration-logos/amazon.png',
            text: 'Bestellungen, Produkte und Marktplatzumsätze',
            side: 'left',
            top: 132,
            path: 'M328 184 H518',
        },
        {
            name: 'eBay',
            logo: '/integration-logos/ebay.png',
            text: 'Verkäufe, Artikel und Kundendaten',
            side: 'left',
            top: 264,
            path: 'M328 316 H392 Q420 316 420 288 V246 Q420 218 448 218 H518',
        },
        {
            name: 'PayPal',
            logo: '/integration-logos/paypal.png',
            text: 'Zahlungen, Gebühren und Transaktionen',
            side: 'right',
            top: 0,
            path: 'M832 52 H768 Q740 52 740 80 V122 Q740 150 712 150 H642',
        },
        {
            name: 'DHL',
            logo: '/integration-logos/dhl.png',
            text: 'Versandstatus und Sendungsverfolgung',
            side: 'right',
            top: 132,
            path: 'M832 184 H642',
        },
        {
            name: 'DATEV',
            logo: '/integration-logos/datev.png',
            text: 'Buchhaltung und Steuerdaten',
            side: 'right',
            top: 264,
            path: 'M832 316 H768 Q740 316 740 288 V246 Q740 218 712 218 H642',
        },
    ];

    return (
        <motion.section id="integrationen" className="commetriq-deferred-section commetriq-deferred-section-integrations relative scroll-mt-28 overflow-hidden bg-white px-4 py-16 md:py-20 lg:px-10 lg:py-16 xl:px-12 xl:py-20">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(229,92,26,0.07),transparent_32%),linear-gradient(180deg,#ffffff_0%,#fffdf9_100%)]" />
            <div className="relative mx-auto max-w-[1200px]">
                <motion.div {...subtleContentReveal} className="mx-auto max-w-3xl text-center">
                    <p className="text-sm font-semibold uppercase tracking-[0.32em] text-orange-600">Integration</p>
                    <h2 className="mt-4 text-4xl font-semibold leading-[1.05] tracking-[-0.055em] text-slate-950 md:text-5xl 2xl:text-6xl">
                        Deine Shopdaten laufen an einem Ort zusammen.
                    </h2>
                        <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 md:text-lg">
                            JTL-Wawi bildet die Basis. Welche Marktplatz-, Zahlungs-, Versand- und Buchhaltungsdaten verfügbar sind, hängt von der eingerichteten Anbindung und den erforderlichen Freigaben ab. Den Umfang klären wir vor dem Test.
                        </p>
                </motion.div>

                <motion.div
                    ref={integrationFlowRef}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                    variants={{
                        hidden: {},
                        show: { transition: { staggerChildren: 0.07, delayChildren: 0.08 } },
                    }}
                    className="relative mx-auto mt-9 min-h-[376px] max-w-[1160px]"
                >
                    <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[340px] w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-orange-100/90 lg:block" />
                    <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[238px] w-[238px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-orange-200/80 lg:block" />
                    <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[156px] w-[156px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-orange-100 bg-orange-50/50 lg:block" />

                    <svg className="pointer-events-none absolute left-1/2 top-0 hidden h-[368px] w-[1160px] -translate-x-1/2 lg:block" viewBox="0 0 1160 368" fill="none" aria-hidden="true">
                        {integrations.map((item, index) => (
                            <g key={`${item.name}-line`}>
                                <path d={item.path} stroke="#fed7aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                <motion.path
                                    d={item.path}
                                    stroke={BRAND}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    initial={{ opacity: 0, pathLength: 0 }}
                                    animate={integrationFlowInView ? { opacity: 0.26, pathLength: 1 } : { opacity: 0, pathLength: 0 }}
                                    transition={{ delay: 0.08 + index * 0.06, duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
                                />
                                <circle cx={item.side === 'left' ? 328 : 832} cy={item.top + 52} r="4.5" fill={BRAND} />
                            </g>
                        ))}
                    </svg>

                    <motion.div
                        style={{ x: '-50%', y: '-50%' }}
                        className="absolute left-1/2 top-[184px] z-20 hidden lg:block"
                        variants={{ hidden: { opacity: 0, scale: 0.86 }, show: { opacity: 1, scale: 1 } }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <motion.div
                            className="relative flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-orange-100 bg-transparent shadow-[0_26px_70px_rgba(229,92,26,0.22)]"
                            initial={false}
                            animate={integrationFlowInView ? { scale: [1, 1.1, 1], boxShadow: ['0 26px 70px rgba(229,92,26,0.22)', '0 34px 86px rgba(229,92,26,0.38)', '0 26px 70px rgba(229,92,26,0.24)'] } : { scale: 1 }}
                            transition={{ delay: 1.05, duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <motion.span
                                className="pointer-events-none absolute inset-0 rounded-full bg-white/25"
                                initial={{ scale: 0.45, opacity: 0 }}
                                animate={integrationFlowInView ? { scale: [0.45, 1.25], opacity: [0.45, 0] } : { scale: 0.45, opacity: 0 }}
                                transition={{ delay: 1.08, duration: 0.62, ease: 'easeOut' }}
                            />
                            <span className="block h-16 w-16 shrink-0 overflow-hidden">
                                <img src="/commetriq-logo-light.webp" alt="commetriq Logo" className="h-16 w-auto max-w-none" loading="lazy" decoding="async" />
                            </span>
                        </motion.div>
                    </motion.div>

                    <div className="grid gap-5 lg:block">
                        {integrations.map((item) => (
                            <motion.article
                                key={item.name}
                                variants={{ hidden: { opacity: 0, y: 18, scale: 0.98 }, show: { opacity: 1, y: 0, scale: 1 } }}
                                transition={{ duration: 0.46, ease: 'easeOut' }}
                                className={cx(
                                    'relative z-10 flex h-[112px] items-center gap-5 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-[0_18px_44px_rgba(15,23,42,0.075)] transition-all hover:-translate-y-1 hover:shadow-[0_24px_58px_rgba(15,23,42,0.1)]',
                                    'lg:absolute lg:w-[328px] lg:[top:var(--card-top)]',
                                    item.side === 'left' ? 'lg:left-0' : 'lg:right-0'
                                )}
                                style={{ '--card-top': `${item.top}px` }}
                            >
                                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm">
                                    {item.logo ? (
                                        <img src={item.logo} alt={item.name + " Logo"} className="h-12 w-12 object-contain" loading="lazy" decoding="async" />
                                    ) : (
                                        <span className="text-lg font-black tracking-[-0.04em] text-slate-950">{item.mark}</span>
                                    )}
                                </span>
                                <span className="min-w-0 flex-1 pr-16">
                                    <span className="block text-base font-semibold text-slate-950">{item.name}</span>
                                    <span className="mt-1 block max-h-12 overflow-hidden text-sm leading-6 text-slate-500">{item.text}</span>
                                </span>
                                <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                    Anbindung
                                </span>
                            </motion.article>
                        ))}
                    </div>

                    <motion.div
                        className="mt-5 flex justify-center lg:hidden"
                        variants={{ hidden: { opacity: 0, scale: 0.9 }, show: { opacity: 1, scale: 1 } }}
                    >
                        <motion.div
                            className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border border-orange-100 bg-transparent shadow-[0_22px_60px_rgba(229,92,26,0.2)]"
                            initial={false}
                            animate={integrationFlowInView ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                            transition={{ delay: 1.05, duration: 0.62, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <span className="block h-16 w-16 shrink-0 overflow-hidden">
                                <img src="/commetriq-logo-light.webp" alt="commetriq Logo" className="h-16 w-auto max-w-none" loading="lazy" decoding="async" />
                            </span>
                        </motion.div>
                    </motion.div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="mx-auto mt-10 flex max-w-[860px] flex-col gap-6 rounded-[1.35rem] border border-orange-100 bg-gradient-to-r from-orange-50 via-white to-orange-50 px-8 py-7 shadow-[0_24px_70px_rgba(229,92,26,0.10)] sm:flex-row sm:items-center sm:justify-between"
                >
                    <div className="flex items-center gap-5">
                        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-orange-600 text-white shadow-[0_16px_34px_rgba(229,92,26,0.26)]">
                            <Plug className="h-8 w-8" strokeWidth={2.3} />
                        </span>
                        <div>
                            <h3 className="text-base font-semibold text-slate-950">Deine Datenquelle fehlt?</h3>
                            <p className="mt-1 max-w-md text-sm leading-6 text-slate-600">
                                Wir klären gemeinsam, wie deine Systeme sicher und sauber angebunden werden.
                            </p>
                        </div>
                    </div>
                    <a href="#ablauf" className="inline-flex shrink-0 items-center justify-center gap-3 rounded-lg bg-orange-600 px-7 py-4 text-sm font-semibold text-white shadow-[0_16px_34px_rgba(229,92,26,0.22)] transition-all hover:-translate-y-0.5 hover:bg-orange-700">
                        So läuft die Anbindung <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                    </a>
                </motion.div>
            </div>
        </motion.section>
    );
};
const LegacySolutionsPlatformSection = () => {
    const tabs = ['Übersicht', 'Profit', 'Lager', 'Versand', 'Retouren'];
    const bullets = [
        [LineChart, 'Marge verstehen', 'Rohertrag und Umsatz in einer Ansicht bewerten.'],
        [Boxes, 'Bestände steuern', 'Topseller, Low Stock und Kapitalbindung früh erkennen.'],
        [Bell, 'Probleme früher erkennen', 'Retouren, Versandrückstände und Auffälligkeiten priorisieren.'],
    ];
    const kpis = [
        [BarChart3, 'Umsatz', '128.540 EUR', '+10,2%'],
        [ShoppingCart, 'Bestellungen', '2.482', '+12,3%'],
        [LineChart, 'Profit', '23.840 EUR', '+14,5%'],
        [Boxes, 'Lagerbestand', '8.760', 'Artikel'],
    ];
    const channels = [
        ['Shop', '56,2%', 'bg-blue-600'],
        ['Amazon', '23,4%', 'bg-orange-500'],
        ['eBay', '12,1%', 'bg-emerald-500'],
        ['Others', '8,3%', 'bg-slate-400'],
    ];
    const tableRows = [
        ['Comfort UP Pumpe', '14.250 EUR', '320', '42,1%'],
        ['KAMAN Dichtungsband', '11.890 EUR', '275', '38,6%'],
        ['Wartungsset CGB-2', '7.950 EUR', '410', '34,2%'],
    ];

    return (
        <motion.section {...sectionReveal} id="solutions" className="relative scroll-mt-28 overflow-hidden bg-white px-4 py-20 md:py-24">
            <div className="pointer-events-none absolute right-0 top-8 h-72 w-72 opacity-50"
                style={{
                    backgroundImage: 'radial-gradient(rgba(148,163,184,0.55) 1px, transparent 1px)',
                    backgroundSize: '18px 18px',
                }}
            />
            <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:items-center">
                <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.32em] text-orange-600">Lösungen</p>
                    <h2 className="mt-6 max-w-xl text-5xl font-semibold leading-[1.06] tracking-[-0.055em] text-slate-950 lg:text-6xl">
                        Alle Bereiche deines Shops in einer Plattform
                    </h2>
                    <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600">
                        Von Umsatz bis Lager verbindet commetriq deine Daten in klaren Modulen für operative und strategische Entscheidungen.
                    </p>

                    <div className="mt-10 flex max-w-xl gap-8 border-b border-slate-200 text-base text-slate-500">
                        {tabs.map((tab, index) => (
                            <button
                                key={tab}
                                type="button"
                                className={cx(
                                    'relative pb-4 font-medium transition-colors hover:text-slate-950',
                                    index === 0 && 'text-slate-950'
                                )}
                            >
                                {tab}
                                {index === 0 && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-orange-600" />}
                            </button>
                        ))}
                    </div>

                    <div className="mt-9 space-y-6">
                        {bullets.map(([Icon, title, text], index) => (
                            <motion.div
                                key={title}
                                initial={{ opacity: 0, x: -18 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.08, duration: 0.48, ease: 'easeOut' }}
                                className="grid max-w-xl grid-cols-[46px_1fr] gap-5"
                            >
                                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-950 shadow-sm">
                                    <Icon className="h-6 w-6" strokeWidth={2.1} />
                                </span>
                                <span>
                                    <span className="block text-lg font-semibold tracking-[-0.02em] text-slate-950">{title}</span>
                                    <span className="mt-1 block text-sm leading-6 text-slate-500">{text}</span>
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, x: 40, scale: 0.985 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.78, ease: 'easeOut' }}
                    className="relative"
                >
                    <div className="absolute right-4 top-1/2 hidden -translate-y-1/2 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-[0_22px_70px_rgba(15,23,42,0.13)] lg:block">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                                <Clock3 className="h-5 w-5" strokeWidth={2.4} />
                            </span>
                            <div>
                                        <p className="text-sm font-semibold text-slate-950">JTL-Daten</p>
                                        <p className="mt-0.5 text-xs text-slate-500">Synchronisierter Datenstand</p>
                            </div>
                        </div>
                    </div>

                    <div className="absolute bottom-24 right-4 hidden rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-[0_22px_70px_rgba(15,23,42,0.13)] lg:block">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                                <Bell className="h-5 w-5" strokeWidth={2.4} />
                            </span>
                            <div>
                                <p className="text-sm font-semibold text-slate-950">Automatische Alerts</p>
                                <p className="mt-0.5 text-xs text-slate-500">Wichtige Änderungen im Blick</p>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-[1.45rem] border border-slate-200 bg-white shadow-[0_34px_90px_rgba(15,23,42,0.13)]">
                        <div className="flex h-12 items-center gap-2 border-b border-slate-200 bg-white px-5">
                            <span className="h-3 w-3 rounded-full bg-red-400" />
                            <span className="h-3 w-3 rounded-full bg-amber-400" />
                            <span className="h-3 w-3 rounded-full bg-emerald-500" />
                        </div>

                        <div className="grid min-h-[500px] bg-slate-50 lg:grid-cols-[168px_1fr]">
                            <aside className="hidden border-r border-slate-200 bg-white px-5 py-6 lg:block">
                                <div className="flex items-center gap-3 text-base font-semibold text-slate-950">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                                        <BarChart3 className="h-5 w-5" strokeWidth={2.6} />
                                    </span>
                                    commetriq
                                </div>
                                <div className="mt-9 space-y-2 text-sm font-medium text-slate-500">
                                    {['Übersicht', 'Profit', 'Lager', 'Versand', 'Retouren'].map((item, index) => (
                                        <div key={item} className={cx('rounded-lg px-3 py-2.5', index === 0 ? 'bg-blue-50 text-blue-700' : 'text-slate-500')}>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-32 space-y-2 text-sm text-slate-500">
                                    <div className="rounded-lg px-3 py-2">Einstellungen</div>
                                    <div className="rounded-lg px-3 py-2">Hilfe</div>
                                </div>
                            </aside>

                            <div className="p-5 md:p-6">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div>
                                        <p className="text-lg font-semibold text-slate-950">Guten Tag, Max Mustermann</p>
                                        <p className="mt-1 text-sm text-slate-500">Hier ist dein Überblick für heute.</p>
                                    </div>
                                    <div className="flex gap-3">
                                        <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600">01.06. - 30.06.2024</span>
                                        <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600">Filter</span>
                                    </div>
                                </div>

                                <div className="mt-5 grid gap-3 md:grid-cols-4">
                                    {kpis.map(([Icon, label, value, trend]) => (
                                        <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                                    <Icon className="h-4 w-4" strokeWidth={2.5} />
                                                </span>
                                                <span className="text-xs font-semibold text-slate-600">{label}</span>
                                            </div>
                                            <p className="mt-4 text-xl font-semibold tracking-tight text-slate-950">{value}</p>
                                            <p className="mt-1 text-xs font-semibold text-emerald-600">{trend}</p>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_0.9fr]">
                                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                        <p className="text-sm font-semibold text-slate-950">Umsatzentwicklung</p>
                                        <div className="mt-5 h-40">
                                            <MiniLine points={[32, 45, 38, 54, 76, 68, 88, 94, 72, 86, 98, 110, 96, 118, 150]} height={150} strokeWidth={3} color="#2563eb" />
                                        </div>
                                    </div>

                                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                        <p className="text-sm font-semibold text-slate-950">Umsatz nach Kanal</p>
                                        <div className="mt-5 grid grid-cols-[132px_1fr] items-center gap-5">
                                            <DonutChart
                                                size={126}
                                                stroke={18}
                                                value="128k"
                                                label="Gesamt"
                                                segments={[
                                                    { label: 'Shop', value: 56, color: '#2563eb' },
                                                    { label: 'Amazon', value: 24, color: BRAND },
                                                    { label: 'eBay', value: 12, color: '#22c55e' },
                                                    { label: 'Others', value: 8, color: '#94a3b8' },
                                                ]}
                                            />
                                            <div className="space-y-3">
                                                {channels.map(([label, value, color]) => (
                                                    <div key={label} className="flex items-center justify-between gap-3 text-xs">
                                                        <span className="flex items-center gap-2 font-medium text-slate-600">
                                                            <span className={cx('h-2.5 w-2.5 rounded-full', color)} />
                                                            {label}
                                                        </span>
                                                        <span className="font-semibold text-slate-950">{value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                    <p className="text-sm font-semibold text-slate-950">Top Produkte nach Umsatz</p>
                                    <div className="mt-4 overflow-hidden rounded-lg border border-slate-100">
                                        <div className="grid grid-cols-[1fr_110px_110px_90px] bg-slate-50 px-3 py-2 text-[11px] font-semibold text-slate-400">
                                            <span>Produkt</span>
                                            <span>Umsatz</span>
                                            <span>Bestellungen</span>
                                            <span>Marge</span>
                                        </div>
                                        {tableRows.map((row) => (
                                            <div key={row[0]} className="grid grid-cols-[minmax(0,1fr)_110px_110px_90px] border-t border-slate-100 px-3 py-2 text-xs text-slate-700">
                                                {row.map((cell, index) => <span key={cell} className={index === 0 ? 'truncate pr-3' : ''}>{cell}</span>)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.section>
    );
};

const WorkflowSection = () => {
    const workflowRef = useRef(null);
    const flowVisualRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: flowVisualRef,
        offset: ['start 82%', 'center 54%'],
    });
    const inputProgress = useTransform(scrollYProgress, [0.04, 0.34], [0, 1]);
    const outputProgress = useTransform(scrollYProgress, [0.32, 0.58], [0, 1]);
    const outputFade = useTransform(scrollYProgress, [0.28, 0.48], [0.35, 1]);
    const inputHeadOpacity = useTransform(inputProgress, [0.92, 1], [0, 1]);
    const outputHeadOpacity = useTransform(outputProgress, [0.92, 1], [0, 1]);
    const outputGlow = useTransform(
        outputProgress,
        [0.42, 0.82],
        ['0 16px 42px rgba(15,23,42,0.08)', '0 28px 80px rgba(229,92,26,0.24)']
    );
    const databaseScale = useTransform(scrollYProgress, [0.2, 0.38, 0.62], [0.96, 1.04, 1]);

    const steps = [
        ['01', 'JTL- und Shopdaten bündeln', 'Aufträge, Positionen, Artikel, Lager, Retouren und Kunden laufen in eine gemeinsame Analysebasis.'],
        ['02', 'KPIs automatisch verdichten', 'Nettoumsatz, Rohertrag, Marge, Bestellungen, Retourenquote und Bestand werden pro Zeitraum berechnet.'],
        ['03', 'Entscheidungen priorisieren', 'Das Dashboard zeigt, welche Kanäle, Artikel, Regionen und Kunden heute wirklich relevant sind.'],
    ];
    const sources = [
        ['Aufträge', ShoppingCart, '3.421'],
        ['Artikel', Boxes, '18k SKUs'],
        ['Kunden', Users, '1,28k'],
        ['Retouren', RefreshCw, '4,2%'],
    ];

    return (
        <motion.section {...sectionReveal} id="ablauf" ref={workflowRef} className="relative scroll-mt-28 px-4 py-24 md:py-28 lg:py-24">
            <div className="mx-auto max-w-7xl">
                <div className="space-y-12">
                    <div className="text-center">
                        <SectionHeader center pill="So funktioniert es" title="Vom JTL Wawi Rohdatensatz zu einer klaren Tagespriorität." text="Das Dashboard erklärt nicht nur Kennzahlen. Es zeigt den Ablauf, mit dem ein Shopbetreiber jeden Tag schneller über Umsatz, Rohertrag, Lagerbestand und Retouren entscheidet." />

                        <div className="mx-auto mt-10 grid max-w-6xl gap-0 border-y border-slate-200 md:grid-cols-3">
                            {steps.map(([number, title, text]) => (
                                <div key={title} className="border-b border-slate-200 py-6 text-left last:border-b-0 md:border-b-0 md:border-r md:px-8 md:last:border-r-0">
                                    <p className="text-xs font-semibold text-orange-600">{number}</p>
                                    <div>
                                        <p className="mt-3 text-sm font-semibold text-slate-950">{title}</p>
                                        <p className="mt-2 text-xs leading-5 text-slate-500">{text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="relative">
                        <div>
                            <div className="pointer-events-none absolute -inset-10 -z-10 rounded-[3rem] bg-[radial-gradient(circle_at_24%_20%,rgba(229,92,26,0.11),transparent_34%),radial-gradient(circle_at_84%_80%,rgba(15,23,42,0.06),transparent_30%)] blur-2xl" />
                            <motion.div
                                ref={flowVisualRef}
                                initial={{ opacity: 0, y: 34, scale: 0.98 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                viewport={{ once: true, margin: '-80px' }}
                                transition={{ duration: 0.8, ease: 'easeOut' }}
                                className="relative rounded-[2.1rem] border border-slate-200 bg-white/[0.88] p-6 shadow-[0_26px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl md:p-8"
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Datenfluss</p>
                                        <h3 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-slate-950">Aus vielen Tabellen wird ein Cockpit.</h3>
                                    </div>
                                    <span className="hidden rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 sm:inline-flex">täglich nutzbar</span>
                                </div>

                                <div className="relative mt-10 min-h-[780px] lg:min-h-[560px]">
                                    <div className="absolute left-0 top-0 hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 lg:block">Eingaben</div>
                                    <div className="absolute right-[17rem] top-0 hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 lg:block">Ausgaben (Cockpit)</div>

                                    <div className="grid gap-4 lg:absolute lg:left-0 lg:top-12 lg:w-[280px]">
                                        {sources.map(([label, Icon, value], index) => (
                                            <motion.div
                                                key={label}
                                                initial={{ opacity: 0, x: -18 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: index * 0.08, duration: 0.55, ease: 'easeOut' }}
                                                className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Icon className="h-7 w-7 shrink-0" strokeWidth={3} style={{ color: BRAND }} />
                                                    <div>
                                                        <p className="text-base font-semibold text-slate-950">{label}</p>
                                                        <p className="mt-1 text-sm font-semibold text-slate-400">{value}</p>
                                                    </div>
                                                </div>
                                                <FileSpreadsheet className="h-5 w-5 text-slate-300" strokeWidth={2.4} />
                                            </motion.div>
                                        ))}
                                    </div>

                                    <div className="relative mt-8 h-[300px] lg:mt-0 lg:h-[500px]">
                                        <svg className="absolute inset-0 hidden h-full w-full overflow-visible lg:block" viewBox="0 0 1200 520" preserveAspectRatio="none" fill="none" aria-hidden="true">
                                            <path d="M280 86H350C420 86 402 244 492 244" stroke="rgba(226,232,240,0.95)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                                            <path d="M280 176H368C426 176 420 244 492 244" stroke="rgba(226,232,240,0.95)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                                            <path d="M280 266H372C430 266 422 244 492 244" stroke="rgba(226,232,240,0.95)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                                            <path d="M280 356H350C420 356 402 244 492 244" stroke="rgba(226,232,240,0.95)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                                            <path d="M588 244H700" stroke="rgba(226,232,240,0.95)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                                            <FlowArrow d="M280 86H350C420 86 402 244 492 244" progress={inputProgress} />
                                            <FlowArrow d="M280 176H368C426 176 420 244 492 244" progress={inputProgress} />
                                            <FlowArrow d="M280 266H372C430 266 422 244 492 244" progress={inputProgress} />
                                            <FlowArrow d="M280 356H350C420 356 402 244 492 244" progress={inputProgress} />
                                            <FlowArrow d="M588 244H700" progress={outputProgress} width={2.5} />
                                            {[
                                                ['M484 238L496 244L484 250', inputProgress, inputHeadOpacity, BRAND],
                                                ['M694 238L706 244L694 250', outputProgress, outputHeadOpacity, BRAND],
                                            ].map(([d, progress, opacity, color]) => (
                                                <motion.path key={d} d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ pathLength: progress, opacity }} />
                                            ))}
                                        </svg>
                                        <motion.div
                                            style={{ scale: databaseScale }}
                                            className="relative mx-auto my-8 flex h-24 w-24 items-center justify-center rounded-[1.55rem] bg-slate-950 text-white shadow-[0_22px_50px_rgba(15,23,42,0.22)] lg:absolute lg:left-[45%] lg:top-[186px] lg:z-10 lg:my-0 lg:-translate-x-1/2"
                                        >
                                            <div className="absolute inset-0 rounded-[1.55rem] bg-[linear-gradient(135deg,rgba(15,23,42,0.98),rgba(2,6,23,0.92))]" />
                                            <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-white/[0.15] bg-white/5">
                                                <Database className="h-8 w-8" strokeWidth={2.5} />
                                            </div>
                                        </motion.div>

                                        <motion.div style={{ opacity: outputFade }} className="grid gap-4 lg:absolute lg:right-0 lg:top-12 lg:w-[470px]">
                                            <motion.div style={{ boxShadow: outputGlow }} className="rounded-[1.6rem] border border-slate-200 bg-white/95 p-4">
                                                <div className="mb-4 flex items-center gap-3">
                                                    <span className="h-3 w-3 rounded-full bg-slate-100" />
                                                    <span className="h-2.5 w-14 rounded-full bg-slate-100" />
                                                    <span className="h-2.5 w-12 rounded-full bg-slate-100" />
                                                    <span className="h-2.5 w-10 rounded-full bg-slate-100" />
                                                    <span className="ml-auto h-2.5 w-28 rounded-full bg-slate-100" />
                                                </div>
                                                <div className="grid h-[210px] grid-cols-[1.2fr_0.95fr] gap-4">
                                                    <div className="rounded-2xl border border-slate-100 p-4">
                                                        <span className="block h-2.5 w-16 rounded-full bg-slate-200" />
                                                        <div className="mt-5 h-24">
                                                            <MiniLine points={[16, 24, 21, 34, 30, 42, 39, 58, 66]} color={BRAND} height={96} strokeWidth={2.7} />
                                                        </div>
                                                        <div className="mt-4 grid grid-cols-3 gap-2">
                                                            <span className="h-2 rounded-full bg-slate-200" />
                                                            <span className="h-2 rounded-full bg-slate-100" />
                                                            <span className="h-2 rounded-full bg-slate-200" />
                                                            <span className="h-2 rounded-full bg-slate-100" />
                                                            <span className="h-2 rounded-full bg-slate-200" />
                                                            <span className="h-2 rounded-full bg-slate-100" />
                                                        </div>
                                                    </div>
                                                    <div className="grid gap-4">
                                                        <div className="rounded-2xl border border-slate-100 p-4">
                                                            <DonutChart
                                                                size={92}
                                                                stroke={12}
                                                                label=""
                                                                value=""
                                                                segments={[
                                                                    { label: 'A', value: 58, color: BRAND },
                                                                    { label: 'B', value: 27, color: '#CBD5E1' },
                                                                    { label: 'C', value: 15, color: '#F4B39A' },
                                                                ]}
                                                            />
                                                        </div>
                                                        <div className="overflow-hidden rounded-2xl border border-slate-100 p-4">
                                                            <PulsingBars values={[24, 36, 44, 52, 64, 78]} className="h-14" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                            <div className="grid gap-4 sm:grid-cols-3">
                                                {[
                                                    ['Nettoumsatz', '184k EUR', '+22,4%'],
                                                    ['Marge', '31,8%', '+4,6%'],
                                                    ['Low Stock', '12 SKUs', 'prüfen'],
                                                ].map(([label, value, delta]) => (
                                                    <div key={label} className="rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-[0_14px_34px_rgba(15,23,42,0.07)]">
                                                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
                                                        <p className="mt-3 text-xl font-semibold tracking-tight text-slate-950">{value}</p>
                                                        <p className={cx('mt-2 text-xs font-semibold', delta === 'prüfen' ? 'text-red-500' : 'text-emerald-600')}>{delta}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};


const WorkMomentSection = () => {
    const imageUrl = 'https://images.unsplash.com/photo-1622151834677-70f982c9adef?q=80&w=786&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';
    const signals = [
        { label: 'Nettoumsatz', value: 184, suffix: 'k EUR', decimals: 0, trend: '+22,4%', tone: 'green', direction: 'up', position: 'left-5 top-6 md:-left-8 md:top-12' },
        { label: 'Low Stock', value: 12, suffix: ' SKUs', decimals: 0, trend: 'kritisch', tone: 'red', direction: 'up', position: 'right-5 top-24 md:-right-10 md:top-28' },
        { label: 'Retourenquote', value: 4.2, suffix: '%', decimals: 1, trend: '-0,8%', tone: 'green', direction: 'down', position: 'left-8 bottom-8 md:left-14 md:bottom-12' },
    ];
    const headlineStats = [
        { value: 3421, suffix: '', label: 'Bestellungen', trend: '+11,8%', tone: 'green', direction: 'up', decimals: 0 },
        { value: 31.8, suffix: '%', label: 'Rohertrag', trend: '+4,6%', tone: 'green', direction: 'up', decimals: 1 },
        { value: 1.28, suffix: 'k', label: 'Kunden', trend: '-2,1%', tone: 'red', direction: 'down', decimals: 2 },
    ];

    return (
        <motion.section {...sectionReveal} className="px-4 py-24">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
                <div>
                    <Pill>Im Alltag</Pill>
                    <h2 className="mt-6 text-4xl font-semibold leading-[1.08] tracking-[-0.055em] text-slate-950 sm:text-5xl lg:text-6xl">
                        Das Dashboard ist für den Moment gebaut, in dem Entscheidungen anstehen.
                    </h2>
                    <p className="mt-6 text-base leading-8 text-slate-500">
                        Morgens reicht ein Blick: Umsatz, Marge, Bestand und Retouren werden nicht als Datenwand gezeigt, sondern als klare Prioritäten für Einkauf, Support und Management.
                    </p>
                    <div className="mt-10 grid max-w-xl grid-cols-3 border-y border-slate-200 py-6">
                        {headlineStats.map(({ value, suffix, label, trend, tone, direction, decimals }, index) => (
                            <div key={label} className={cx('px-4 first:pl-0', index < 2 && 'border-r border-slate-200')}>
                                <p className="text-2xl font-semibold tracking-tight text-slate-950">
                                    <AnimatedNumber value={value} suffix={suffix} decimals={decimals} loop={false} />
                                </p>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
                                    <TrendLabel value={trend} tone={tone} direction={direction} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative">
                    <div className="pointer-events-none absolute -inset-12 -z-10 opacity-60">
                        <div className="absolute left-6 top-16 h-px w-64 rotate-[-12deg] bg-gradient-to-r from-transparent via-orange-200 to-transparent" />
                        <div className="absolute right-0 top-36 h-px w-80 rotate-[16deg] bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
                        <div className="absolute bottom-24 left-16 h-px w-72 rotate-[8deg] bg-gradient-to-r from-transparent via-orange-100 to-transparent" />
                    </div>
                    <div className="relative overflow-hidden rounded-[1.8rem] border border-slate-200 bg-white p-2 shadow-[0_26px_80px_rgba(15,23,42,0.12)]">
                        <img
                            src={imageUrl}
                            alt="Shopbetreiber arbeitet am Laptop mit Dashboard-Daten"
                            className="h-[500px] w-full rounded-[1.35rem] object-cover"
                        />
                        <div className="pointer-events-none absolute inset-2 rounded-[1.35rem] ring-1 ring-white/40" />
                    </div>

                    {signals.map(({ label, value, suffix, decimals, trend, tone, direction, position }, index) => (
                        <motion.div
                            key={label}
                            initial={{ opacity: 0, y: index === 1 ? 26 : 18, scale: 0.96 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.7, delay: 0.15 + index * 0.12, ease: 'easeOut' }}
                            className={cx('absolute min-w-44 rounded-[1.15rem] border border-white/80 bg-white/[0.92] px-5 py-4 shadow-[0_18px_46px_rgba(15,23,42,0.18)] backdrop-blur-xl', position)}
                            style={{
                                backgroundImage: 'linear-gradient(135deg,rgba(255,255,255,0.96),rgba(255,255,255,0.82))',
                            }}
                        >
                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
                            <div className="mt-2 flex items-end gap-3">
                                <p className="text-xl font-semibold tracking-tight text-slate-950">
                                    <AnimatedNumber value={value} suffix={suffix} decimals={decimals} loop={false} />
                                </p>
                                <div className="pb-0.5">
                                    <TrendLabel value={trend} tone={tone} direction={direction} />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </motion.section>
    );
};

const MobileMockupSection = () => {
    const floatingCards = [
        {
            label: 'Nettoumsatz',
            value: 184,
            suffix: 'k EUR',
            trend: '+22,4%',
            tone: 'green',
            direction: 'up',
            icon: BarChart3,
            visual: 'line',
            className: 'left-[3%] top-[105px] xl:left-[7%]',
            from: { x: 355, y: 170, rotate: 8 },
        },
        {
            label: 'Warnsignale',
            value: 12,
            suffix: ' SKUs',
            trend: 'prüfen',
            tone: 'red',
            direction: 'up',
            icon: Boxes,
            visual: 'list',
            className: 'right-[3%] top-[105px] xl:right-[7%]',
            from: { x: -355, y: 170, rotate: -8 },
        },
        {
            label: 'Kanalvergleich',
            value: 68.9,
            suffix: 'k EUR',
            trend: '+8,6%',
            tone: 'green',
            direction: 'up',
            icon: ShoppingCart,
            visual: 'bars',
            className: 'left-[5%] bottom-[80px] xl:left-[9%]',
            from: { x: 335, y: -220, rotate: -7 },
        },
        {
            label: 'Rohertrag',
            value: 31.8,
            suffix: '%',
            trend: '+4,6%',
            tone: 'green',
            direction: 'up',
            icon: LineChart,
            visual: 'line-soft',
            className: 'right-[5%] bottom-[80px] xl:right-[9%]',
            from: { x: -335, y: -220, rotate: 7 },
        },
    ];
    const kpis = [
        ['Bestellungen', 3421, '', '+11,8%'],
        ['Rohertrag', 31.8, '%', '+4,6%'],
        ['Retouren', 4.2, '%', '-0,8%'],
        ['Low Stock', 12, ' SKUs', 'prüfen'],
    ];
    const channels = [
        ['Amazon', '/amazon.png', '42,8k', 72],
        ['eBay', '/ebay.png', '16,4k', 44],
        ['Shop', '/logo192.png', '9,7k', 31],
    ];
    const renderFloatingVisual = (visual) => {
        if (visual === 'bars') {
            return (
                <div className="mt-5 flex h-24 items-end gap-2 border-t border-white/50 pt-4">
                    {[58, 34, 72, 48, 64, 28, 52].map((height, index) => (
                        <motion.div
                            key={index}
                            initial={{ height: 0 }}
                            whileInView={{ height: `${height}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.75, delay: 0.45 + index * 0.05, ease: 'easeOut' }}
                            className={cx('flex-1 rounded-t-lg', index === 2 ? 'bg-orange-500' : 'bg-orange-200/75')}
                        />
                    ))}
                </div>
            );
        }

        if (visual === 'list') {
            return (
                <div className="mt-5 space-y-3 border-t border-white/50 pt-4">
                    {[
                        ['Low Stock', '12'],
                        ['Retouren', '4,2%'],
                        ['Artikel prüfen', '3'],
                    ].map(([name, value], index) => (
                        <div key={name} className="flex items-center justify-between text-sm">
                            <span className="font-medium text-slate-500">{name}</span>
                            <span className={cx('font-semibold', index === 0 ? 'text-red-500' : 'text-slate-950')}>{value}</span>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="mt-5 h-12 border-t border-white/50 pt-3">
                <MiniLine
                    points={visual === 'line-soft' ? [26, 31, 29, 36, 34, 42, 39] : [20, 25, 24, 34, 39, 46, 52]}
                    height={50}
                    strokeWidth={3}
                    color={visual === 'line-soft' ? '#111827' : BRAND}
                />
            </div>
        );
    };

    return (
        <motion.section {...sectionReveal} className="relative overflow-hidden bg-[#f7f1ee] px-4 py-24 md:py-28">
            <div className="pointer-events-none absolute inset-x-0 top-[34%] z-0 h-[360px] -translate-y-1/2 overflow-visible">
                <svg className="h-full w-full overflow-visible" viewBox="0 0 1600 360" preserveAspectRatio="none" fill="none" aria-hidden="true">
                    <motion.path
                        d="M-90 178C120 62 304 54 486 148C646 231 764 295 962 195C1154 98 1326 80 1690 120"
                        stroke="rgba(229,92,26,0.12)"
                        strokeWidth="30"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 1 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 1.45, ease: 'easeOut' }}
                    />
                    <motion.path
                        d="M-90 178C120 62 304 54 486 148C646 231 764 295 962 195C1154 98 1326 80 1690 120"
                        stroke="rgba(255,255,255,0.55)"
                        strokeWidth="10"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 1 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 1.45, delay: 0.08, ease: 'easeOut' }}
                    />
                </svg>
            </div>
            <div className="mx-auto max-w-7xl">
                <SectionHeader
                    center
                    pill="Mobile Ansicht"
                    title="Die wichtigsten Shop-Signale bleiben auch unterwegs greifbar."
                    text="commetriq ist kein Desktop-only Bericht: Umsatzanalyse, Retourenquote, Low Stock und Rohertrag bleiben als kompakter Morgencheck lesbar, wenn du nicht am großen Bildschirm sitzt."
                />

                <div className="relative mt-14 min-h-[720px] py-8">
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute left-1/2 top-20 h-[540px] w-[540px] -translate-x-1/2 rounded-full bg-white/70 blur-3xl" />
                    </div>

                    {floatingCards.map(({ label, value, suffix, trend, tone, direction, icon: Icon, visual, className, from }, index) => (
                        <motion.div
                            key={label}
                            initial={{ opacity: 0, x: from.x, y: from.y, rotate: from.rotate, scale: 0.78, filter: 'blur(6px)' }}
                            whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1, filter: 'blur(0px)' }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ type: 'spring', stiffness: 72, damping: 18, mass: 0.9, delay: 0.18 + index * 0.08 }}
                            className={cx('absolute z-20 hidden w-60 rounded-[1.45rem] border border-white/75 bg-white/50 p-5 shadow-[0_22px_60px_rgba(15,23,42,0.12)] backdrop-blur-2xl lg:block', className)}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/70 shadow-sm">
                                        <Icon className="h-4 w-4" strokeWidth={3} style={{ color: BRAND }} />
                                    </span>
                                    <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
                                    <p className="mt-2 text-xl font-semibold tracking-tight text-slate-950">
                                        <AnimatedNumber value={value} suffix={suffix} decimals={label === 'Retourenquote' || label === 'Rohertrag' || label === 'Kanalvergleich' ? 1 : 0} loop={false} />
                                    </p>
                                    </div>
                                </div>
                                <TrendLabel value={trend} tone={tone} direction={direction} />
                            </div>
                            {renderFloatingVisual(visual)}
                        </motion.div>
                    ))}

                    <motion.div
                        initial={{ opacity: 0, y: 38, scale: 0.98 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.85, ease: 'easeOut' }}
                        className="relative z-10 mx-auto flex justify-center"
                    >
                        <div className="relative rotate-[-2deg] rounded-[3.25rem] bg-gradient-to-br from-slate-800 via-slate-950 to-black p-3 shadow-[0_34px_100px_rgba(15,23,42,0.26)]">
                            <div className="pointer-events-none absolute inset-0 rounded-[3.25rem] bg-gradient-to-br from-white/18 via-transparent to-transparent" />
                            <div className="absolute -right-1 top-32 h-20 w-1 rounded-r-full bg-slate-800" />
                            <div className="absolute -left-1 top-28 h-12 w-1 rounded-l-full bg-slate-800" />
                            <div className="absolute -left-1 top-44 h-16 w-1 rounded-l-full bg-slate-800" />

                            <div className="relative h-[632px] w-[306px] overflow-hidden rounded-[2.65rem] border border-slate-800 bg-white sm:h-[680px] sm:w-[330px]">
                                <div className="absolute left-1/2 top-3 z-20 h-7 w-28 -translate-x-1/2 rounded-full bg-slate-950" />
                                <div className="flex items-center justify-between px-7 pb-3 pt-5 text-[11px] font-semibold text-slate-950">
                                    <span>9:41</span>
                                    <div className="flex items-center gap-1.5">
                                        <span className="h-2.5 w-4 rounded-sm border border-slate-950" />
                                        <span className="h-2 w-2 rounded-full bg-slate-950" />
                                    </div>
                                </div>

                                <div className="relative h-full overflow-hidden bg-[radial-gradient(circle_at_50%_0%,rgba(229,92,26,0.14),transparent_28%),linear-gradient(180deg,#fff_0%,#fbfaf9_46%,#f7f1ee_100%)] px-4 pb-24 pt-2">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">commetriq</p>
                                            <p className="text-xl font-semibold tracking-[-0.04em] text-slate-950">Morgencheck</p>
                                        </div>
                                        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500 text-sm font-semibold text-white">W</span>
                                    </div>

                                    <div className="relative mt-5 overflow-hidden rounded-[1.8rem] bg-slate-950 p-5 text-white shadow-[0_18px_40px_rgba(15,23,42,0.24)]">
                                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_15%,rgba(229,92,26,0.38),transparent_38%),linear-gradient(135deg,rgba(255,255,255,0.10),transparent_48%)]" />
                                        <div className="relative">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Nettoumsatz</p>
                                                <p className="mt-2 text-3xl font-semibold tracking-[-0.05em]">
                                                    <AnimatedNumber value={184} suffix="k" loop={false} />
                                                </p>
                                            </div>
                                            <TrendLabel value="+22,4%" tone="green" direction="up" />
                                        </div>
                                        <div className="mt-5 h-16">
                                            <MiniLine points={[18, 24, 21, 32, 38, 44, 57]} height={58} strokeWidth={4} color={BRAND} />
                                        </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 grid grid-cols-2 gap-3">
                                        {kpis.map(([label, value, suffix, delta]) => (
                                            <div key={label} className="rounded-[1.25rem] border border-white/80 bg-white/[0.78] p-3 shadow-[0_10px_28px_rgba(15,23,42,0.05)] backdrop-blur-xl">
                                                <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
                                                <p className="mt-2 text-lg font-semibold tracking-tight text-slate-950">
                                                    <AnimatedNumber value={value} suffix={suffix} decimals={label === 'Rohertrag' || label === 'Retouren' ? 1 : 0} loop={false} />
                                                </p>
                                                <p className={cx('mt-1 text-[10px] font-semibold', delta === 'prüfen' ? 'text-red-500' : 'text-emerald-600')}>{delta}</p>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 rounded-[1.5rem] border border-white/80 bg-white/[0.82] p-4 shadow-[0_12px_30px_rgba(15,23,42,0.06)] backdrop-blur-xl">
                                        <div className="mb-3 flex items-center justify-between">
                                            <p className="text-sm font-semibold text-slate-950">Kanalmix</p>
                                            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-orange-600">Live</span>
                                        </div>
                                        <div className="grid grid-cols-[86px_1fr] items-center gap-3">
                                            <div className="relative h-[86px] w-[86px]">
                                                <motion.div
                                                    initial={{ rotate: -90, opacity: 0 }}
                                                    whileInView={{ rotate: 0, opacity: 1 }}
                                                    viewport={{ once: true }}
                                                    transition={{ duration: 0.9, ease: 'easeOut' }}
                                                    className="absolute inset-0 rounded-full shadow-[0_12px_28px_rgba(229,92,26,0.16)]"
                                                    style={{ background: 'conic-gradient(#E55C1A 0deg 224deg,#111827 224deg 310deg,#F5B49C 310deg 360deg)' }}
                                                />
                                                <div className="absolute inset-[13px] rounded-full bg-white" />
                                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                    <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">Total</span>
                                                    <span className="text-sm font-semibold text-slate-950">68,9k</span>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                {channels.map(([name, logo, value, width]) => (
                                                    <div key={name}>
                                                        <div className="mb-1 flex items-center justify-between">
                                                            <img src={logo} alt={name} className="h-4 w-9 object-contain object-left" />
                                                            <span className="text-[11px] font-semibold text-slate-950">{value}</span>
                                                        </div>
                                                        <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                                                            <motion.div
                                                                initial={{ width: 0 }}
                                                                whileInView={{ width: `${width}%` }}
                                                                viewport={{ once: true }}
                                                                transition={{ duration: 0.85, delay: width / 240, ease: 'easeOut' }}
                                                                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-300"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 rounded-[1.4rem] border border-orange-100 bg-white/[0.86] p-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)] backdrop-blur-xl">
                                        <div className="mb-2 h-1.5 w-10 rounded-full bg-orange-500" />
                                        <p className="text-xs font-semibold text-slate-950">Nächste Aktion</p>
                                        <p className="mt-1 text-xs leading-5 text-slate-500">12 Low-Stock SKUs prüfen, bevor Topseller ausverkaufen.</p>
                                    </div>

                                    <div className="absolute inset-x-8 bottom-4 flex items-center justify-between rounded-full border border-slate-100 bg-white/90 px-5 py-3 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur">
                                        {[BarChart3, Boxes, RefreshCw, Users].map((Icon, index) => (
                                            <Icon key={index} className={cx('h-4 w-4', index === 0 ? 'text-orange-500' : 'text-slate-400')} strokeWidth={3} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <div className="mt-6 grid gap-4 md:hidden">
                        {floatingCards.slice(0, 3).map(({ label, value, suffix, trend, tone, direction }) => (
                            <div key={label} className="rounded-3xl border border-slate-200 bg-white p-5">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
                                        <p className="mt-2 text-2xl font-semibold text-slate-950">
                                            <AnimatedNumber value={value} suffix={suffix} decimals={label === 'Retouren' ? 1 : 0} loop={false} />
                                        </p>
                                    </div>
                                    <TrendLabel value={trend} tone={tone} direction={direction} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </motion.section>
    );
};


const ProductShowcase = () => {
    const proofStats = [
        ['1505', 'Bestellungen', 'im Zeitraum'],
        ['31,8%', 'Rohertrag', 'sichtbar'],
        ['12', 'Low-Stock SKUs', 'priorisiert'],
        ['4,2%', 'Retourenquote', 'mit Kontext'],
    ];
    const proofSignals = [
        ['Dashboard-Ansicht', 'Kennzahlen direkt im Kontext', Sparkles],
        ['Kanal-Mix', 'Amazon, eBay und Shop', ShoppingCart],
        ['Top Produkte', 'nach Profit sortiert', PackageCheck],
        ['Retouren-Signale', 'Grund und Risiko', RefreshCw],
    ];

    return (
        <motion.section {...sectionReveal} id="dashboard" className="relative scroll-mt-28 overflow-hidden bg-slate-50 px-4 py-24">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_12%,rgba(15,23,42,0.055),transparent_30%),radial-gradient(circle_at_82%_68%,rgba(229,92,26,0.055),transparent_28%)]" />
            <div className="mx-auto max-w-7xl">
                <div className="mx-auto max-w-3xl text-center">
                    <Pill>Produktansicht</Pill>
                    <h2 className="mt-5 text-4xl font-semibold leading-[1.05] tracking-[-0.045em] text-slate-950 sm:text-5xl">
                        Ein Dashboard, das morgens die richtigen Entscheidungen nach vorne holt.
                    </h2>
                    <p className="mt-4 text-base text-slate-500">
                        Umsatz, Profit, Produkte, Kunden und Retouren in einer Oberfläche.
                    </p>
                </div>

                <div className="mt-10 grid gap-5 lg:grid-cols-[1.2fr_0.8fr] lg:items-stretch">
                    <motion.figure
                        initial={{ opacity: 0, y: 24, scale: 0.985 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.75, ease: 'easeOut' }}
                        className="relative min-h-[360px] overflow-hidden rounded-[1.6rem] border border-slate-200 bg-slate-100 shadow-[0_26px_70px_rgba(15,23,42,0.12)]"
                    >
                        <img
                            src="/macbook_hero_1500x1000.png"
                            alt="commetriq Dashboard Screenshot"
                            className="absolute inset-0 h-full w-full object-cover object-center"
                            loading="lazy"
                            decoding="async"
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/80 to-transparent p-6 text-white">
                            <p className="text-sm font-semibold text-orange-200">commetriq Cockpit</p>
                            <p className="mt-1 text-2xl font-semibold">Umsatz, Profit, Produkte, Kunden, Retouren.</p>
                        </div>
                    </motion.figure>

                    <div className="grid gap-4">
                        <div className="grid grid-cols-2 gap-3">
                            {proofStats.map(([value, label, helper], index) => (
                                <motion.div
                                    key={label}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.07, duration: 0.45, ease: 'easeOut' }}
                                    className="rounded-[1rem] border border-slate-200 bg-white p-4 shadow-sm"
                                >
                                    <p className="text-2xl font-semibold text-slate-950">{value}</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-700">{label}</p>
                                    <p className="mt-1 text-xs text-slate-400">{helper}</p>
                                </motion.div>
                            ))}
                        </div>

                        <div className="rounded-[1rem] border border-slate-950 bg-slate-950 p-5 text-white shadow-[0_22px_60px_rgba(15,23,42,0.18)]">
                            <p className="text-sm font-semibold text-orange-300">Was sichtbar ist</p>
                            <div className="mt-4 grid gap-3">
                                {proofSignals.map(([title, text, Icon]) => (
                                    <div key={title} className="flex items-center gap-3">
                                        <Icon className="h-5 w-5 shrink-0 text-orange-300" strokeWidth={2.5} />
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-white">{title}</p>
                                            <p className="text-xs text-slate-400">{text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};

const LiveScreenshotSection = () => {
    const callouts = [
        ['01', 'KPI-Drilldowns', 'Kennzahlen springen direkt in gefilterte Detailansichten.'],
        ['02', 'Tageskontext', 'Zeitraum und Tagesauswahl bleiben sichtbar, auch bei tieferen Analysen.'],
        ['03', 'Offene Aufträge', 'Backlog und Versandstatus werden in einer Queue priorisiert.'],
    ];

    return (
        <motion.section {...sectionReveal} className="relative overflow-hidden bg-slate-950 px-4 py-24 text-white">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(229,92,26,0.20),transparent_32%),radial-gradient(circle_at_85%_45%,rgba(59,130,246,0.14),transparent_28%)]" />
            <div className="relative mx-auto max-w-7xl">
                <div className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-center">
                    <div>
                        <Pill>Live gerenderte Ansichten</Pill>
                        <h2 className="mt-6 text-4xl font-semibold leading-[1.08] tracking-[-0.045em] text-white sm:text-5xl">
                            Mehr echtes Produkt, weniger Symbolbild.
                        </h2>
                        <p className="mt-6 text-base leading-8 text-slate-300">
                            Die Seite zeigt jetzt nicht nur ein schönes Laptop-Mockup, sondern echte Dashboard-Zustände aus der App. Das macht den Nutzen schneller greifbar.
                        </p>
                        <div className="mt-9 space-y-4">
                            {callouts.map(([number, title, text], index) => (
                                <motion.div
                                    key={title}
                                    initial={{ opacity: 0, x: -18 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.09, duration: 0.55, ease: 'easeOut' }}
                                    className="grid grid-cols-[42px_1fr] gap-4 border-t border-white/10 pt-4"
                                >
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-sm font-semibold text-white">{number}</span>
                                    <span>
                                        <span className="block font-semibold text-white">{title}</span>
                                        <span className="mt-1 block text-sm leading-6 text-slate-300">{text}</span>
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <motion.figure
                        initial={{ opacity: 0, y: 30, rotateX: 8 }}
                        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.85, ease: 'easeOut' }}
                        className="relative"
                    >
                        <motion.div
                            className="absolute -inset-4 rounded-[2rem] bg-orange-500/20 blur-2xl"
                            animate={{ opacity: [0.45, 0.8, 0.45], scale: [1, 1.035, 1] }}
                            transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
                        />
                        <div className="relative overflow-hidden rounded-[1.4rem] border border-white/[0.14] bg-white/[0.08] p-2 shadow-[0_34px_90px_rgba(0,0,0,0.32)] backdrop-blur">
                            <img
                                src="/versand-performance-annotated.png"
                                alt="Annotierte Versand Performance Ansicht in commetriq"
                                className="h-full w-full rounded-[1rem] object-cover"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </motion.figure>
                </div>
            </div>
        </motion.section>
    );
};

const QuestionModules = () => {
    const sliderRef = useRef(null);
    const [sliderProgress, setSliderProgress] = useState(0);
    const modules = [
        {
            title: 'Tagesübersicht',
            label: 'Alles auf einen Blick',
            text: 'Umsatz, Bestellungen, Profit, Lager, Versand und Retouren in einer klaren Startansicht. Warnungen und auffällige Entwicklungen führen direkt zur passenden Detailanalyse.',
            icon: Sparkles,
            visual: 'overview',
            proof: '6 Bereiche · Live-Warnungen',
        },
        {
            title: 'Umsatzanalyse',
            label: 'Entwicklung verstehen',
            text: 'Vergleiche Nettoumsatz, Bestellungen, Warenkorb und Rohertrag über frei wählbare Zeiträume. So erkennst du Wachstum, saisonale Effekte und Abweichungen frühzeitig.',
            icon: BarChart3,
            visual: 'revenue',
            proof: '4 Kern-KPIs · flexible Zeiträume',
        },
        {
            title: 'Profit & Kosten',
            label: 'Marge verbessern',
            text: 'Prüfe Rohertrag, Kosten und Marge bis auf Produkt- und Kanalebene. Fehlende oder noch nicht zugeordnete Kosten klären wir für den konkreten Datenstand.',
            icon: LineChart,
            visual: 'profit',
            proof: 'Marge · Kosten · Kanalvergleich',
        },
        {
            title: 'Kanäle & Marktplätze',
            label: 'Kanäle vergleichen',
            text: 'Stelle Amazon, eBay und deinen Shop nach verfügbaren Umsatz- und Kostenwerten gegenüber. Welche Plattformdaten aktuell sind, prüfen wir je Anbindung.',
            icon: ShoppingCart,
            visual: 'channels',
            proof: 'Amazon · eBay · Onlineshop, je nach Anbindung',
        },
        {
            title: 'Lager & Produkte',
            label: 'Bestände steuern',
            text: 'Erkenne Low Stock, Topseller und unnötig gebundenes Kapital bis zur einzelnen SKU. Priorisiere Nachbestellungen, reduziere Überbestände und sichere die Lieferfähigkeit.',
            icon: Boxes,
            visual: 'inventory',
            proof: 'SKU-genau · Reichweiten',
        },
        {
            title: 'Versand Performance',
            label: 'Aufträge priorisieren',
            text: 'Überwache Rückstände, Picklisten, Versandvolumen und überfällige Aufträge in einer Ansicht. Kritische Bestellungen lassen sich früh erkennen und gezielt priorisieren.',
            icon: PackageCheck,
            visual: 'shipping',
            proof: 'Backlog · Pickliste · SLA',
        },
        {
            title: 'Retourenanalyse',
            label: 'Marge schützen',
            text: 'Analysiere Retourenquote, Problemartikel und Gründe nach Produkt, Kategorie und Kanal. So findest du Margenverluste und konkrete Ansatzpunkte für Einkauf, Sortiment und Support.',
            icon: RefreshCw,
            visual: 'returns',
            proof: 'Quote · Gründe · Margenrisiko',
        },
        {
            title: 'Reports & Exporte',
            label: 'Sauber berichten',
            text: 'Erstelle wiederholbare Reports für Team, Beratung und Monatsabschluss. Exportiere relevante Kennzahlen als Excel, CSV oder PDF und teile verlässliche Entscheidungsgrundlagen.',
            icon: FileSpreadsheet,
            visual: 'reports',
            proof: 'CSV · Excel · PDF',
        },
    ];

    const scrollCards = (direction) => {
        sliderRef.current?.scrollBy({
            left: direction * 484,
            behavior: 'smooth',
        });
    };

    const updateSliderProgress = () => {
        const slider = sliderRef.current;
        if (!slider) return;
        const maxScroll = Math.max(slider.scrollWidth - slider.clientWidth, 1);
        setSliderProgress(slider.scrollLeft / maxScroll);
    };

    const renderVisualHeader = (eyebrow, title, meta, tone = 'slate') => {
        const statusDot = {
            slate: 'bg-slate-400',
            red: 'bg-red-500',
            orange: 'bg-orange-500',
            emerald: 'bg-emerald-500',
        }[tone];

        return (
            <div className="relative flex min-h-[52px] items-start justify-between gap-3 border-b border-slate-200 pb-3">
                <div className="min-w-0">
                    <p className="truncate text-[8px] font-semibold uppercase tracking-[0.12em] text-slate-400">{eyebrow}</p>
                    <p className="mt-1 truncate text-[13px] font-semibold text-slate-950">{title}</p>
                    <p className="mt-1 text-[8px] font-medium text-slate-400">Beispielansicht · Beispieldaten</p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-[8px] font-medium text-slate-500 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                    <span className={cx('h-1.5 w-1.5 rounded-full', statusDot)} />
                    {meta}
                </span>
            </div>
        );
    };
    const renderFeatureVisual = (module) => {
        if (module.visual === 'overview') {
            const overviewMetrics = [
                ['Umsatz', '100.255 €', '+15,5%', 74, 'bg-orange-500'],
                ['Gewinn', '77.090 €', '+18,6%', 66, 'bg-emerald-500'],
                ['Marge', '76,9%', '+1,2 pp', 82, 'bg-slate-700'],
            ];
            const priorities = [
                ['Lager', 'Low Stock', '8', 'bg-red-500'],
                ['Versand', 'Offene Aufträge', '24', 'bg-orange-500'],
                ['Retouren', 'Zu prüfen', '3', 'bg-slate-400'],
            ];

            return (
                <div className="h-full bg-gradient-to-b from-white to-slate-50/60 px-4 py-3.5">
                    {renderVisualHeader('Management Cockpit', 'Heute auf einen Blick', 'Beispiel', 'emerald')}
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        {overviewMetrics.map(([label, value, delta, progress, progressTone], metricIndex) => (
                            <motion.div
                                key={label}
                                initial={{ opacity: 0, y: 6 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.5 }}
                                transition={{ duration: 0.34, delay: metricIndex * 0.06 }}
                                className="min-w-0 rounded-md border border-slate-200 bg-white px-2 py-2 shadow-[0_4px_12px_rgba(15,23,42,0.035)]"
                            >
                                <p className="truncate text-[7px] font-medium uppercase text-slate-400">{label}</p>
                                <p className="mt-1 truncate text-[12px] font-semibold text-slate-950">{value}</p>
                                <div className="mt-1 flex items-center gap-1.5">
                                    <span className="text-[7px] font-medium text-emerald-600">{delta}</span>
                                    <span className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
                                        <motion.span
                                            initial={{ scaleX: 0 }}
                                            whileInView={{ scaleX: 1 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.5, delay: 0.12 + metricIndex * 0.06 }}
                                            className={cx('block h-full origin-left rounded-full', progressTone)}
                                            style={{ width: progress + '%' }}
                                        />
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                    <div className="mt-3 overflow-hidden rounded-md border border-slate-200 bg-white shadow-[0_6px_18px_rgba(15,23,42,0.035)]">
                        <div className="grid grid-cols-[54px_1fr_26px] bg-slate-50 px-2.5 py-1.5 text-[8px] font-medium uppercase text-slate-400">
                            <span>Bereich</span><span>Hinweis</span><span className="text-right">Anz.</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {priorities.map(([area, label, value, dot], priorityIndex) => (
                                <motion.div
                                    key={area}
                                    initial={{ opacity: 0, x: 6 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: 0.12 + priorityIndex * 0.05 }}
                                    className="grid grid-cols-[54px_1fr_26px] items-center px-2.5 py-2 text-[9px]"
                                >
                                    <span className="text-slate-500">{area}</span>
                                    <span className="flex items-center gap-2 font-medium text-slate-700"><span className={cx('h-1.5 w-1.5 rounded-full', dot)} />{label}</span>
                                    <span className="text-right font-semibold text-slate-950">{value}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }
        if (module.visual === 'revenue') {
            const revenueLine = 'M6 88 C34 87 42 70 67 74 C91 79 101 54 128 60 C153 66 166 39 192 45 C217 51 228 26 254 31 C278 36 291 18 314 20';
            const comparisonLine = 'M6 92 C36 91 46 82 72 84 C98 86 110 70 136 73 C164 76 178 58 204 61 C232 64 249 47 276 50 C294 52 304 44 314 43';

            return (
                <div className="h-full bg-gradient-to-b from-white to-slate-50/60 px-4 py-3.5">
                    {renderVisualHeader('Umsatzanalyse', 'Nettoumsatz im Zeitverlauf', '30 Tage')}
                    <div className="mt-3 flex items-end justify-between">
                        <div>
                            <p className="text-[8px] font-medium uppercase text-slate-400">Nettoumsatz</p>
                            <div className="mt-1 flex items-baseline gap-2">
                                <span className="text-lg font-semibold text-slate-950">184.260 €</span>
                                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[8px] font-semibold text-emerald-700"><ArrowUpRight className="h-2.5 w-2.5" />22,4%</span>
                            </div>
                        </div>
                        <div className="flex gap-4 text-right">
                            <div><p className="text-[8px] text-slate-400">Bestellungen</p><p className="mt-0.5 text-[11px] font-semibold">1.842</p></div>
                            <div><p className="text-[8px] text-slate-400">Ø Warenkorb</p><p className="mt-0.5 text-[11px] font-semibold">100,03 €</p></div>
                        </div>
                    </div>
                    <div className="mt-2 rounded-lg border border-slate-200 bg-white px-2 pt-1.5 shadow-[0_6px_18px_rgba(15,23,42,0.035)]">
                        <div className="flex justify-end gap-3 pr-1 text-[7px] text-slate-400">
                            <span className="inline-flex items-center gap-1"><span className="h-px w-3 bg-orange-500" />Aktuell</span>
                            <span className="inline-flex items-center gap-1"><span className="h-px w-3 border-t border-dashed border-slate-400" />Vormonat</span>
                        </div>
                        <svg viewBox="0 0 320 104" className="h-[104px] w-full" role="img" aria-label="Flächendiagramm des Nettoumsatzes im Juni mit Vormonatsvergleich">
                            <defs>
                                <linearGradient id="revenue-area-vivid" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#E55C1A" stopOpacity="0.16" />
                                    <stop offset="100%" stopColor="#E55C1A" stopOpacity="0.01" />
                                </linearGradient>
                            </defs>
                            <path d="M6 14 H314 M6 48 H314 M6 88 H314" fill="none" stroke="#F1F5F9" strokeWidth="1" />
                            <motion.path d={comparisonLine} fill="none" stroke="#94A3B8" strokeWidth="1.3" strokeDasharray="4 4" initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 0.75 }} viewport={{ once: true }} transition={{ duration: 0.7 }} />
                            <path d={revenueLine + ' L314 94 L6 94 Z'} fill="url(#revenue-area-vivid)" />
                            <motion.path d={revenueLine} fill="none" stroke="#E55C1A" strokeWidth="2.5" strokeLinecap="round" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }} />
                            <motion.circle
                                cx="314"
                                cy="20"
                                r="7"
                                fill="#E55C1A"
                                initial={{ opacity: 0.12, scale: 0.8 }}
                                whileInView={{ opacity: [0.12, 0.28, 0.12], scale: [0.8, 1.2, 1] }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, ease: 'easeOut' }}
                            />
                            <circle cx="314" cy="20" r="3.5" fill="#fff" stroke="#E55C1A" strokeWidth="2" />
                            <text x="6" y="102" fill="#94A3B8" fontSize="8">01. Jun</text>
                            <text x="145" y="102" fill="#94A3B8" fontSize="8">15. Jun</text>
                            <text x="282" y="102" fill="#94A3B8" fontSize="8">30. Jun</text>
                        </svg>
                    </div>
                </div>
            );
        }
        if (module.visual === 'profit') {
            const profitRows = [
                ['Rohertrag', '128.540 €', '+12,4%', 'positive'],
                ['Variable Kosten', '82.862 €', '+4,6%', 'negative'],
                ['Nettoprofit', '45.678 €', '+18,6%', 'positive'],
            ];
            const channelMargins = [
                ['Shop', '42,1%', '+2,8 pp'],
                ['Amazon', '38,6%', '+1,9 pp'],
                ['eBay', '34,2%', '+0,7 pp'],
            ];

            return (
                <div className="h-full bg-gradient-to-b from-white to-slate-50/60 px-4 py-3.5">
                    {renderVisualHeader('Profit & Kosten', 'Ergebnisrechnung', 'Jun 2026')}
                    <div className="mt-3 grid grid-cols-[108px_1fr] items-center gap-4">
                        <div className="relative h-[104px] w-[104px]">
                            <svg viewBox="0 0 108 108" className="relative z-10 h-full w-full -rotate-90" role="img" aria-label="Donutdiagramm der Marge">
                                <circle cx="54" cy="54" r="40" fill="none" stroke="#E2E8F0" strokeWidth="9" />
                                <motion.circle cx="54" cy="54" r="40" fill="none" stroke="#E55C1A" strokeWidth="9" strokeLinecap="round" pathLength="100" strokeDasharray="35.6 64.4" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} />
                            </svg>
                            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
                                <span className="text-lg font-semibold text-slate-950">35,6%</span>
                                <span className="text-[8px] uppercase text-slate-400">Marge</span>
                                <span className="mt-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[7px] font-semibold text-emerald-700"><ArrowUpRight className="h-2.5 w-2.5" />3,1 pp</span>
                            </div>
                        </div>
                        <dl className="space-y-1">
                            {profitRows.map(([label, value, delta, tone], rowIndex) => (
                                <motion.div
                                    key={label}
                                    initial={{ opacity: 0, x: 6 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: rowIndex * 0.06 }}
                                    className={cx('grid grid-cols-[1fr_auto] items-center gap-2 rounded-md px-2 py-1.5', rowIndex === 2 ? 'bg-emerald-50/70' : 'bg-white/80')}
                                >
                                    <dt className="text-[8px] text-slate-500">{label}</dt>
                                    <dd className="text-right">
                                        <p className={cx('text-[9px] font-semibold', label === 'Nettoprofit' ? 'text-emerald-700' : 'text-slate-950')}>{value}</p>
                                        <span className={cx('mt-0.5 inline-flex items-center justify-end gap-0.5 text-[7px] font-medium', tone === 'negative' ? 'text-red-600' : 'text-emerald-700')}>
                                            <ArrowUpRight className="h-2.5 w-2.5" />{delta}
                                        </span>
                                    </dd>
                                </motion.div>
                            ))}
                        </dl>
                    </div>
                    <div className="mt-2 grid grid-cols-3 divide-x divide-slate-200 border-t border-slate-200 pt-2">
                        {channelMargins.map(([name, margin, delta], channelIndex) => (
                            <motion.div key={name} initial={{ opacity: 0, y: 4 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.12 + channelIndex * 0.05 }} className="text-center">
                                <p className="text-[7px] text-slate-400">{name}</p>
                                <p className="mt-0.5 text-[9px] font-semibold text-slate-700">{margin}</p>
                                <p className="mt-0.5 inline-flex items-center gap-0.5 text-[7px] font-medium text-emerald-700"><ArrowUpRight className="h-2.5 w-2.5" />{delta}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            );
        }
        if (module.visual === 'channels') {
            const channels = [
                { name: 'Amazon', logo: '/integration-logos/amazon.png', share: '62%', revenue: '42,8k €', delta: '+18,4%', className: 'col-span-3 row-span-2 border-orange-200 bg-orange-50/55' },
                { name: 'eBay', logo: '/integration-logos/ebay.png', share: '24%', revenue: '16,4k €', delta: '+6,2%', className: 'col-span-2 border-slate-300 bg-slate-100' },
                { name: 'Shop', logo: '/logo192.png', share: '14%', revenue: '9,7k €', delta: '+3,1%', className: 'col-span-2 border-slate-200 bg-white' },
            ];

            return (
                <div className="h-full bg-gradient-to-b from-white to-slate-50/60 px-4 py-3.5">
                    {renderVisualHeader('Kanäle & Marktplätze', 'Umsatzverteilung', '30 Tage')}
                    <div className="mt-3 flex items-baseline justify-between">
                        <p className="text-[9px] text-slate-500">Anteil am Kanalumsatz</p>
                        <p className="rounded-full bg-white/90 px-2 py-1 text-[11px] font-semibold text-slate-950 shadow-sm">68.900 € <span className="ml-1 text-[8px] font-medium text-emerald-600">+12,8%</span></p>
                    </div>
                    <div className="mt-2.5 grid h-[150px] grid-cols-5 grid-rows-2 gap-1.5" role="img" aria-label="Treemap der Umsatzanteile nach Verkaufskanal">
                        {channels.map((channel, channelIndex) => (
                            <motion.div
                                key={channel.name}
                                initial={{ opacity: 0, scale: 0.97 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.34, delay: channelIndex * 0.06 }}
                                className={cx('flex min-w-0 flex-col justify-between overflow-hidden rounded-md border p-2.5 shadow-[0_5px_14px_rgba(15,23,42,0.035)]', channel.className)}
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className="flex h-8 w-12 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white px-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
                                        <img src={channel.logo} alt={channel.name + ' Logo'} className="max-h-6 w-full object-contain" />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="truncate text-[9px] font-semibold text-slate-700">{channel.name}</p>
                                        <p className="mt-0.5 text-[8px] font-medium text-emerald-700">{channel.delta}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-lg font-semibold text-slate-950">{channel.share}</p>
                                    <p className="mt-0.5 text-[8px] text-slate-500">{channel.revenue}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            );
        }
        if (module.visual === 'inventory') {
            const inventoryRows = [
                ['Kopfhörer Pro', ['critical', 'critical', 'warning', 'neutral', 'neutral'], '3 T.'],
                ['Kaffeeautomat', ['warning', 'warning', 'warning', 'neutral', 'neutral'], '5 T.'],
                ['Sneaker X1', ['neutral', 'neutral', 'healthy', 'healthy', 'healthy'], '18 T.'],
            ];
            const cellTone = {
                critical: 'border-red-200 bg-red-100',
                warning: 'border-amber-200 bg-amber-100',
                neutral: 'border-slate-200 bg-slate-100',
                healthy: 'border-emerald-200 bg-emerald-100',
            };

            return (
                <div className="h-full bg-gradient-to-b from-white to-slate-50/60 px-4 py-3.5">
                    {renderVisualHeader('Lager & Produkte', 'Bestandsreichweite', '12 prüfen', 'red')}
                    <div className="mt-3 grid grid-cols-3 divide-x divide-slate-200 border-b border-slate-200 pb-3">
                        {[['Low Stock', '12'], ['Ausverkauft', '3'], ['SKU gesamt', '8.760']].map(([label, value], metricIndex) => (
                            <div key={label} className="px-2 first:pl-0 last:pr-0">
                                <p className="truncate text-[8px] text-slate-400">{label}</p>
                                <p className={cx('mt-0.5 text-[12px] font-semibold', metricIndex < 2 ? 'text-red-600' : 'text-slate-950')}>{value}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-2">
                        <div className="grid grid-cols-[78px_1fr_30px] items-center gap-2 px-1 text-[7px] font-medium uppercase text-slate-400">
                            <span>Artikel</span><span className="flex justify-between"><span>Heute</span><span>30 Tage</span></span><span className="text-right">RW</span>
                        </div>
                        <div className="mt-1 divide-y divide-slate-100">
                            {inventoryRows.map(([name, cells, range]) => (
                                <div key={name} className="grid grid-cols-[78px_1fr_30px] items-center gap-2 px-1 py-2">
                                    <span className="truncate text-[8px] font-medium text-slate-600">{name}</span>
                                    <div className="grid grid-cols-5 gap-1">
                                        {cells.map((tone, cellIndex) => <span key={name + '-' + cellIndex} className={cx('h-3.5 border', cellTone[tone])} />)}
                                    </div>
                                    <span className="text-right text-[8px] font-semibold text-slate-700">{range}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (module.visual === 'shipping') {
            const shippingMetrics = [
                ['In Bearbeitung', '24'],
                ['Heute versendet', '91'],
                ['Überfällig', '6'],
            ];
            const funnelStages = [
                ['Eingegangen', '128', 'M8 4 H352 L326 28 H34 Z', '#0F172A', '#FFFFFF'],
                ['Pickliste', '112', 'M34 32 H326 L300 56 H60 Z', '#475569', '#FFFFFF'],
                ['Verpackt', '103', 'M60 60 H300 L274 84 H86 Z', '#EA580C', '#FFFFFF'],
                ['Versendet', '91', 'M86 88 H274 L250 112 H110 Z', '#FDBA74', '#7C2D12'],
            ];

            return (
                <div className="h-full bg-gradient-to-b from-white to-slate-50/60 px-4 py-3.5">
                    {renderVisualHeader('Versand Performance', 'Auftragsstatus heute', '6 kritisch', 'orange')}
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        {shippingMetrics.map(([label, value], metricIndex) => (
                            <div key={label} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
                                <p className="truncate text-[7px] font-medium uppercase text-slate-400">{label}</p>
                                <p className={cx('mt-0.5 text-[12px] font-semibold', metricIndex === 2 ? 'text-red-600' : 'text-slate-950')}>{value}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-2.5 rounded-md border border-slate-200 bg-slate-50/40 px-2.5 py-1.5">
                        <div className="flex items-center justify-between px-1 text-[8px] font-medium">
                            <span className="text-slate-600">Versand-Funnel</span>
                            <span className="text-slate-400">Aufträge</span>
                        </div>
                        <svg viewBox="0 0 360 116" className="mt-1 h-[116px] w-full" role="img" aria-label="Funnel der heutigen Versandaufträge">
                            {funnelStages.map(([label, value, path, fill, textColor], stageIndex) => (
                                <motion.g key={label} initial={{ opacity: 0, y: -3 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: stageIndex * 0.06 }}>
                                    <path d={path} fill={fill} />
                                    <text x="180" y={20 + stageIndex * 28} textAnchor="middle" fill={textColor} fontSize="9" fontWeight="600">{label} · {value}</text>
                                </motion.g>
                            ))}
                        </svg>
                    </div>
                </div>
            );
        }
        if (module.visual === 'returns') {
            const returnReasons = [
                ['Größe / Variante', 210, 34, '#E55C1A'],
                ['Defekt', 156, 74, '#F59E0B'],
                ['Erwartung', 92, 48, '#475569'],
            ];

            return (
                <div className="h-full bg-gradient-to-b from-white to-slate-50/60 px-4 py-3.5">
                    {renderVisualHeader('Retourenanalyse', 'Häufigkeit & Margenrisiko', '30 Tage')}
                    <div className="mt-3 grid grid-cols-2 divide-x divide-slate-200">
                        <div><p className="text-[8px] text-slate-400">Retourenquote</p><p className="mt-0.5 text-[13px] font-semibold text-slate-950">4,2%</p></div>
                        <div className="pl-4"><p className="text-[8px] text-slate-400">Gebundene Marge</p><p className="mt-0.5 text-[13px] font-semibold text-orange-700">8.420 €</p></div>
                    </div>
                    <svg viewBox="0 0 300 126" className="mt-2 h-[126px] w-full" role="img" aria-label="Streudiagramm der Retourengründe nach Häufigkeit und Margenrisiko">
                        <path d="M30 12 V101 H290 M30 56 H290 M116 12 V101 M203 12 V101" fill="none" stroke="#E2E8F0" strokeWidth="1" />
                        <text x="30" y="117" fill="#94A3B8" fontSize="8">niedrig</text>
                        <text x="256" y="117" fill="#94A3B8" fontSize="8">häufig</text>
                        <text x="3" y="17" fill="#94A3B8" fontSize="8">Risiko</text>
                        {returnReasons.map(([label, x, y, fill]) => (
                            <g key={label}>
                                <circle cx={x} cy={y} r="5" fill={fill} />
                                <circle cx={x} cy={y} r="9" fill="none" stroke={fill} strokeOpacity="0.18" />
                                <text x={x + 9} y={y + 3} fill="#475569" fontSize="8" fontWeight="500">{label}</text>
                            </g>
                        ))}
                    </svg>
                </div>
            );
        }

        if (module.visual === 'reports') {
            const reportRows = [
                ['Umsatz nach Kanal', 'CSV', 'Täglich', 'Heute 08:30'],
                ['Retouren nach Artikel', 'PDF', 'Wöchentlich', 'Fr 09:00'],
                ['Profit & Marge', 'XLSX', 'Monatlich', '01. 07.'],
            ];

            return (
                <div className="h-full bg-gradient-to-b from-white to-slate-50/60 px-4 py-3.5">
                    {renderVisualHeader('Reports & Exporte', 'Geplante Ausgaben', '3 aktiv', 'emerald')}
                    <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
                        <div className="grid grid-cols-[1fr_42px_58px_64px] gap-2 bg-slate-50 px-2.5 py-2 text-[7px] font-medium uppercase text-slate-400">
                            <span>Report</span><span>Format</span><span>Intervall</span><span>Zeitpunkt</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {reportRows.map(([name, format, interval, schedule]) => (
                                <div key={name} className="grid grid-cols-[1fr_42px_58px_64px] items-center gap-2 px-2.5 py-3 text-[8px]">
                                    <span className="truncate font-medium text-slate-700">{name}</span>
                                    <span className="text-slate-500">{format}</span>
                                    <span className="text-slate-500">{interval}</span>
                                    <span className="text-right text-slate-400">{schedule}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[8px] text-slate-400">
                        <span>Letzter Lauf: heute 08:30</span>
                        <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Erfolgreich</span>
                    </div>
                </div>
            );
        }

        return null;
    };
    return (
        <motion.section
            id="module"
            initial={false}
            className="commetriq-deferred-section commetriq-deferred-section-large relative z-10 scroll-mt-24 overflow-hidden border-y border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_48%,#ffffff_100%)] py-16 md:py-24"
        >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[440px] bg-[radial-gradient(circle_at_50%_0%,rgba(249,115,22,0.09),transparent_62%)]" />
            <div className="relative mx-auto max-w-[1440px] px-5 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center justify-center gap-4"
                >
                    <span className="h-px w-10 bg-gradient-to-r from-transparent to-orange-400 sm:w-16" />
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-orange-600 sm:text-base">Dashboard-Funktionen</p>
                    <span className="h-px w-10 bg-gradient-to-l from-transparent to-orange-400 sm:w-16" />
                </motion.div>

                <div className="mt-10 grid gap-9 text-left lg:grid-cols-[1.04fr_0.96fr] lg:items-center lg:gap-14 xl:gap-20">
                    <motion.h2
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.35 }}
                        transition={{ duration: 0.58, delay: 0.04, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-[720px] text-4xl font-semibold leading-[1.03] tracking-[-0.045em] text-slate-950 sm:text-5xl lg:text-[3.6rem] xl:text-[4.1rem]"
                    >
                        <span className="block">Was du mit</span>
                        <span className="mt-2 flex items-center justify-start gap-3 sm:gap-5">
                            <img src="/commetriq-logo-light.webp" alt="commetriq" width="2852" height="500" className="h-9 w-auto object-contain sm:h-11 lg:h-12 xl:h-[54px]" />
                            <span>konkret</span>
                        </span>
                        <span className="block">machen kannst.</span>
                    </motion.h2>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:pt-4"
                    >
                        <p className="max-w-2xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
                            Jeder Bereich beantwortet eine operative Frage und führt von der Kennzahl zur nächsten Entscheidung. Scrolle durch die Module und sieh, welche Daten und Aktionen darin stecken.
                        </p>
                        <div className="mt-7 flex w-fit max-w-full flex-wrap items-center justify-start gap-x-5 gap-y-2 rounded-full border border-slate-200 bg-white/85 px-5 py-3 text-xs font-semibold text-slate-600 shadow-[0_12px_32px_rgba(15,23,42,0.06)] backdrop-blur sm:gap-x-7 sm:px-7">
                            {['8 operative Module', 'Live-Kennzahlen', 'Klare nächste Schritte'].map((item, itemIndex) => (
                                <span key={item} className="inline-flex items-center gap-2">
                                    <span className={cx('h-1.5 w-1.5 rounded-full', itemIndex === 0 ? 'bg-orange-500' : itemIndex === 1 ? 'bg-emerald-500' : 'bg-slate-400')} />
                                    {item}
                                </span>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            <div className="relative mx-auto mt-14 max-w-[1500px] px-5 sm:px-6 lg:mt-16 lg:px-8">
                <div
                    ref={sliderRef}
                    onScroll={updateSliderProgress}
                    className="-mx-4 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-3 sm:-mx-6 sm:px-6 lg:-mx-8 lg:gap-6 lg:px-8"
                    style={{ scrollbarWidth: 'none' }}
                >
                    {modules.map((module, index) => {
                        const Icon = module.icon;
                        const iconClass = 'border border-slate-200 bg-white text-orange-600';
                        const labelClass = 'tracking-[0.06em] text-slate-500';

                        const heading = (
                            <div className="mt-5 flex items-start justify-between gap-4">
                                <div className="flex min-w-0 items-start gap-3">
                                    <span className={cx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', iconClass)}>
                                        <Icon className="h-[18px] w-[18px]" strokeWidth={2.1} />
                                    </span>
                                    <div className="min-w-0">
                                        <p className={cx('text-[10px] font-semibold uppercase', labelClass)}>{module.label}</p>
                                        <h3 className="mt-1 text-xl font-semibold tracking-[-0.02em] text-slate-950">{module.title}</h3>
                                    </div>
                                </div>
                                <span className="pt-1 text-[10px] font-medium tabular-nums text-slate-300">{String(index + 1).padStart(2, '0')}</span>
                            </div>
                        );
                        const description = (
                            <p className="mt-4 text-sm leading-6 text-slate-600">{module.text}</p>
                        );
                        const footer = (
                            <div className="mt-auto -mx-5 -mb-5 border-t border-slate-200 bg-slate-50/70 px-5 py-4 sm:-mx-6 sm:-mb-6 sm:px-6">
                                <p className="text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-400">Schwerpunkte</p>
                                <div className="mt-2 flex items-center justify-between gap-3">
                                    <p className="min-w-0 truncate text-xs font-medium text-slate-600">{module.proof}</p>
                                    <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-slate-700">
                                        Im Dashboard <ArrowUpRight className="h-3.5 w-3.5 text-orange-600" />
                                    </span>
                                </div>
                            </div>
                        );
                        const visual = (
                            <DeferredModuleVisual sliderRef={sliderRef} label={module.title}>
                                {renderFeatureVisual(module)}
                            </DeferredModuleVisual>
                        );

                        return (
                            <motion.article
                                key={module.title}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                                className="group relative flex min-h-[500px] w-[84vw] max-w-[380px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-orange-200 bg-white p-5 shadow-[0_12px_34px_rgba(15,23,42,0.065)] transition-shadow duration-500 hover:shadow-[0_20px_48px_rgba(15,23,42,0.10)] sm:p-6"
                            >
                                <motion.span
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-0 z-20 rounded-2xl border border-orange-500/90"
                                    initial={{
                                        opacity: 0.42,
                                        boxShadow: 'inset 0 0 0 0 rgba(249,115,22,0.08)',
                                    }}
                                    whileInView={{
                                        opacity: [0.42, 1, 0.58],
                                        boxShadow: [
                                            'inset 0 0 0 0 rgba(249,115,22,0.08)',
                                            'inset 0 0 0 1px rgba(249,115,22,0.18)',
                                            'inset 0 0 0 0 rgba(249,115,22,0.08)',
                                        ],
                                    }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.82, delay: index * 0.05, ease: 'easeOut' }}
                                />
                                {visual}
                                {heading}
                                {description}
                                {footer}
                            </motion.article>
                        );
                    })}
                </div>

                <ModuleSliderControls
                    onPrevious={() => scrollCards(-1)}
                    onNext={() => scrollCards(1)}
                    progress={sliderProgress}
                />
            </div>
        </motion.section>
    );
};
const ActionsSection = () => {
    const actions = [
        ['Nachbestellen', 'Low Stock und Topseller zuerst prüfen.', PackageCheck, 'Heute'],
        ['Kanal budgetieren', 'Umsatz und Marge pro Plattform vergleichen.', BarChart3, 'Diese Woche'],
        ['Retourengrund prüfen', 'Problemartikel und Gründe priorisieren.', RefreshCw, 'Support'],
        ['Report exportieren', 'Sales Facts, Artikel, Kunden und Kanäle teilen.', FileSpreadsheet, 'Freitag'],
    ];

    return (
        <motion.section {...sectionReveal} className="px-4 py-24">
            <div className="mx-auto max-w-6xl bg-slate-950 text-white">
                <div className="grid lg:grid-cols-[0.8fr_1.2fr]">
                    <div className="p-8 md:p-12">
                        <Pill>Was mache ich danach?</Pill>
                        <h2 className="mt-6 text-4xl font-semibold leading-tight tracking-[-0.045em] sm:text-5xl">Aus Zahlen wird ein Arbeitstag.</h2>
                        <p className="mt-5 text-base leading-8 text-slate-300">Der Nutzen endet nicht beim Chart. commetriq zeigt, welche Entscheidung Einkauf, Support oder Management als nächstes treffen sollte.</p>
                        <div className="mt-8 grid grid-cols-2 gap-8 border-t border-white/10 pt-6">
                            <div>
                                <p className="text-3xl font-semibold">3</p>
                                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Tasks</p>
                            </div>
                            <div>
                                <p className="text-3xl font-semibold">58k</p>
                                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Impact EUR</p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="h-full border-t border-white/10 p-6 md:p-8 lg:border-l lg:border-t-0">
                            <div>
                                <div className="mb-4 flex items-center justify-between">
                                    <p className="text-sm font-semibold">Operative Queue</p>
                                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-orange-300">Live</span>
                                </div>
                                <div className="divide-y divide-white/10 border-y border-white/10">
                                    {actions.map(([title, text, Icon, tag], index) => (
                                        <motion.div
                                            key={title}
                                            animate={{ x: [0, index % 2 ? 5 : -5, 0] }}
                                            transition={{ duration: 4.5 + index * 0.25, repeat: Infinity, ease: 'easeInOut' }}
                                            className="py-5 text-white"
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex gap-4">
                                                    <Icon className="mt-1 h-5 w-5 shrink-0 text-orange-300" strokeWidth={3} />
                                                    <div>
                                                        <p className="font-semibold">{title}</p>
                                                        <p className="mt-1 text-sm leading-6 text-slate-300">{text}</p>
                                                    </div>
                                                </div>
                                                <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{tag}</span>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};

const ReportsTrustSection = () => {
    const reports = ['Sales Facts', 'Artikel + Bestand', 'Kunden', 'Kanäle'];
    const trust = [
        ['Mandantenfähig', ShieldCheck],
        ['Rollen & Zugriff', Lock],
        ['Auth + Policies', Database],
        ['Schnelle Filter', Zap],
    ];

    return (
        <motion.section {...sectionReveal} id="reports" className="scroll-mt-28 px-4 py-20">
            <div className="mx-auto max-w-6xl">
                <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
                    <div className="rounded-[1.3rem] border border-slate-200 bg-white p-7 shadow-sm">
                        <Pill>Reports</Pill>
                        <h2 className="mt-5 text-3xl font-semibold leading-tight text-slate-950">Exportierbar, wenn es ins Team muss.</h2>
                        <div className="mt-7 grid grid-cols-2 gap-3">
                            {reports.map((item, index) => (
                                <motion.div
                                    key={item}
                                    animate={{ y: [0, index % 2 ? 4 : -4, 0] }}
                                    transition={{ duration: 4 + index * 0.2, repeat: Infinity, ease: 'easeInOut' }}
                                    className="rounded-[1rem] border border-slate-200 bg-slate-50 p-4"
                                >
                                    <Check className="h-5 w-5 text-orange-600" strokeWidth={3} />
                                    <p className="mt-3 text-sm font-semibold text-slate-800">{item}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-[1.3rem] border border-slate-950 bg-slate-950 p-7 text-white shadow-[0_24px_70px_rgba(15,23,42,0.18)]">
                        <Pill>Trust</Pill>
                        <h2 className="mt-5 text-3xl font-semibold leading-tight text-white">SaaS-ready ohne Drama.</h2>
                        <div className="mt-7 grid grid-cols-2 gap-3">
                            {trust.map(([title, Icon], index) => (
                                <motion.div
                                    key={title}
                                    initial={{ opacity: 0, y: 14 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.08, duration: 0.45 }}
                                    className="rounded-[1rem] border border-white/10 bg-white/[0.04] p-4"
                                >
                                    <Icon className="h-5 w-5 text-orange-300" strokeWidth={2.8} />
                                    <p className="mt-3 text-sm font-semibold text-white">{title}</p>
                                </motion.div>
                            ))}
                        </div>
                        <motion.div
                            animate={{ opacity: [0.55, 1, 0.55] }}
                            transition={{ duration: 2.4, repeat: Infinity }}
                            className="mt-6 rounded-[1rem] border border-white/10 bg-black/20 px-4 py-3 font-mono text-xs text-slate-300"
                        >
                            sync ready / orders, items, returns, customers
                        </motion.div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};

const PricingSection = ({ onAction }) => {
    const [billing, setBilling] = useState('monthly');
    const isYearly = billing === 'yearly';
    const plans = [
        {
            name: 'Basis',
            monthly: 49,
            yearly: 490,
            yearlyEquivalent: 41,
            description: 'Für kleine JTL-Shops',
            volume: 'Bis 500 Bestellungen / Monat',
            cta: 'Gespräch anfragen',
            features: [
                'Dashboard & KPI-Übersicht',
                'Umsatzanalyse',
                'Artikelanalyse',
                'Kundenübersicht',
                'Lager Basis',
                '1 Nutzer',
            ],
        },
        {
            name: 'Wachstum',
            monthly: 129,
            yearly: 1290,
            yearlyEquivalent: 108,
            description: 'Für wachsende Shops',
            volume: 'Bis 2.500 Bestellungen / Monat',
            cta: 'Gespräch anfragen',
            featured: true,
            features: [
                'Alles aus Basis',
                'Margenanalyse',
                'Retourenanalyse',
                'Alerts & Benachrichtigungen',
                'CSV / Excel Exporte',
                '3 Nutzer',
            ],
        },
        {
            name: 'Skalierung',
            monthly: 349,
            yearly: 3490,
            yearlyEquivalent: 291,
            description: 'Für größere Händler',
            volume: 'Bis 10.000 Bestellungen / Monat',
            cta: 'Gespräch anfragen',
            features: [
                'Alles aus Wachstum',
                'Multi-Shop',
                'Rollen & Rechte',
                'Scheduled Reports',
                'Mehr Historie',
                '10 Nutzer',
            ],
        },
    ];

    return (
        <motion.section {...sectionReveal} id="preise" className="commetriq-deferred-section commetriq-deferred-section-pricing relative scroll-mt-28 overflow-hidden border-y border-slate-200 bg-white px-4 py-16 md:py-20 lg:py-16 xl:py-20">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(229,92,26,0.075),transparent_28%),radial-gradient(circle_at_86%_74%,rgba(15,23,42,0.045),transparent_28%),linear-gradient(180deg,#ffffff_0%,#f8fafc_52%,#ffffff_100%)]" />
            <div className="relative mx-auto max-w-[1160px]">
                <SectionHeader
                    center
                    pill="Preise"
                    title="Einfache Preise für wachsende JTL-Shops"
                    text="Wähle das Paket, das zu deinem Bestellvolumen und Analysebedarf passt. Angebot ausschließlich für Geschäftskunden. Alle Preise netto zuzüglich gesetzlicher Umsatzsteuer, soweit diese anfällt."
                />

                <div className="mx-auto mt-10 grid w-full max-w-sm grid-cols-2 rounded-2xl border border-slate-200 bg-slate-100 p-1 text-sm font-semibold text-slate-500 shadow-inner">
                    {[
                        ['monthly', 'Monatlich'],
                        ['yearly', 'Jährlich'],
                    ].map(([value, label]) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => setBilling(value)}
                            aria-pressed={billing === value}
                            className={cx(
                                'rounded-xl px-5 py-3 transition-all',
                                billing === value
                                    ? 'bg-white text-slate-950 shadow-[0_8px_24px_rgba(15,23,42,0.08)]'
                                    : 'hover:text-slate-950'
                            )}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <div className="mt-10 grid gap-4 sm:gap-5 lg:grid-cols-[repeat(3,minmax(0,340px))] lg:justify-center">
                    {plans.map((plan, index) => (
                        <motion.article
                            key={plan.name}
                            initial={{ opacity: 0, y: 28, scale: 0.98 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.58, delay: index * 0.08, ease: 'easeOut' }}
                            className={cx(
                                'relative flex min-h-[625px] flex-col overflow-hidden rounded-[1.15rem] border bg-white p-6 shadow-[0_18px_54px_rgba(15,23,42,0.08)]',
                                plan.featured
                                    ? 'border-orange-500 ring-1 ring-orange-500/20 shadow-[0_24px_70px_rgba(229,92,26,0.14)]'
                                    : 'border-slate-200'
                            )}
                        >
                            {plan.featured && (
                                <div className="absolute inset-x-0 top-0 flex justify-center">
                                    <span className="rounded-b-xl bg-orange-600 px-5 py-2 text-xs font-semibold text-white shadow-[0_12px_26px_rgba(229,92,26,0.28)]">
                                        Beliebteste Wahl
                                    </span>
                                </div>
                            )}

                            <div className={cx('text-center', plan.featured && 'pt-5')}>
                                <h3 className="text-2xl font-semibold tracking-[-0.04em] text-slate-950">{plan.name}</h3>
                                <div className="mt-6 flex items-end justify-center gap-2 text-slate-950">
                                    <span className="text-[44px] font-semibold leading-none tracking-[-0.06em]">
                                        {isYearly ? plan.yearly.toLocaleString('de-DE') : plan.monthly}
                                    </span>
                                    <span className="pb-1 text-2xl font-semibold">€</span>
                                    <span className="pb-2 text-sm font-medium text-slate-500">
                                        / {isYearly ? 'Jahr' : 'Monat'}
                                    </span>
                                </div>
                                <p className="mt-4 text-base font-medium text-slate-600">{plan.description}</p>
                                {isYearly && (
                                    <p className="mt-2 text-sm font-semibold text-orange-600">
                                        entspricht ca. {plan.yearlyEquivalent} € / Monat
                                    </p>
                                )}
                            </div>

                            <div className="my-7 h-px bg-slate-200" />

                            <p className="text-center text-base font-semibold text-slate-950">{plan.volume}</p>
                            <ul className="mb-8 mt-6 space-y-3.5">
                                {plan.features.map((feature) => (
                                    <li key={feature} className="flex items-start gap-3 text-sm leading-6 text-slate-700">
                                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-orange-500 text-orange-600">
                                            <Check className="h-3.5 w-3.5" strokeWidth={3} />
                                        </span>
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                type="button"
                                onClick={onAction}
                                className={cx(
                                    'mt-auto inline-flex items-center justify-center gap-2 rounded-lg border px-5 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5',
                                    plan.featured
                                        ? 'border-orange-600 bg-orange-600 text-white shadow-[0_18px_36px_rgba(229,92,26,0.22)] hover:bg-orange-700'
                                        : 'border-orange-500 bg-white text-orange-600 hover:bg-orange-50'
                                )}
                            >
                                {plan.cta}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </motion.article>
                    ))}
                </div>

                <div className="mx-auto mt-8 grid max-w-5xl gap-x-10 gap-y-4 border-t border-slate-200 pt-6 text-sm text-slate-500 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                        ['Persönliche Demo', 'Wir klären zuerst deinen Anwendungsfall und die verfügbaren Daten.'],
                        ['Kündigungsfrist', 'Ein Monat zum Ende der Abrechnungsperiode.'],
                        ['Support inklusive', 'Einrichtung und Fragen per E-Mail.'],
                        ['Setup nach Bedarf', 'Datenquellen und Umfang klären wir im Erstkontakt.'],
                    ].map(([title, text]) => (
                        <div key={title}>
                            <p className="text-sm font-semibold text-slate-950">{title}</p>
                            <p className="mt-1 leading-6">{text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </motion.section>
    );
};

const FAQItem = ({ q, a }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-3xl border border-slate-200 bg-white px-6 py-5">
            <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-6 text-left">
                <span className="text-base font-semibold text-slate-950">{q}</span>
                <ChevronDown className={cx('h-5 w-5 text-slate-400 transition-transform', open && 'rotate-180')} />
            </button>
            {open && <p className="mt-4 text-sm leading-7 text-slate-500">{a}</p>}
        </div>
    );
};

const FAQSection = () => (
    <motion.section {...sectionReveal} id="faq" className="scroll-mt-28 px-4 py-24">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <SectionHeader pill="FAQ" title="Fragen, die Shopbetreiber direkt haben." />
            <div className="space-y-4">
                <FAQItem q="Welche Daten sehe ich im JTL-Wawi-Dashboard zuerst?" a="Die Startansicht zeigt Nettoumsatz, Bestellungen, Rohertrag, Marge, Retourenquote, Kanäle und operative Warnsignale wie Low Stock." />
                <FAQItem q="Ist commetriq nur Reporting?" a="Nein. commetriq ist auf operative Entscheidungen ausgelegt: Nachbestellen, Kanal prüfen, Retourengrund analysieren oder Report teilen." />
                <FAQItem q="Kann ein Shop getrennt von anderen Shops laufen?" a="Ja. Die Struktur ist mandantenfähig gedacht: Nutzer, Rollen und Datenzugriff werden pro Shop sauber getrennt." />
            </div>
        </div>
    </motion.section>
);

const FinalCTA = ({ onAction }) => (
    <motion.section {...sectionReveal} className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="relative isolate mx-auto max-w-[1500px] overflow-hidden rounded-[26px] border border-slate-800 bg-[#030a16] text-white shadow-[0_30px_90px_rgba(6,16,32,0.16)] lg:min-h-[650px]">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 50% 65% at 78% 54%, rgba(222,87,18,0.26), transparent 76%), radial-gradient(ellipse 65% 85% at 48% 45%, rgba(14,32,59,0.48), transparent 80%), linear-gradient(105deg, #020814 0%, #061122 55%, #080a12 100%)' }} />

            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative z-20 flex flex-col items-start justify-center px-7 pb-6 pt-14 sm:px-12 lg:min-h-[650px] lg:w-[53%] lg:px-[clamp(52px,5vw,82px)] lg:py-16">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#ff842e] sm:text-sm">Persönliche Demo</p>
                <h2 className="mt-5 text-[clamp(2.6rem,5vw,5rem)] font-bold leading-[1.01] tracking-[-0.055em]">
                    <span className="block">Prüfe Commetriq</span>
                    <span className="block">in 20 Minuten<span className="text-[#f97316]">.</span></span>
                </h2>
                <p className="mt-7 max-w-[620px] text-lg leading-snug text-slate-300 sm:text-xl lg:text-[clamp(1.15rem,1.65vw,1.65rem)]">
                    Sieh am Beispielauftrag, welche Kosten erfasst sind und was vom Umsatz übrig bleibt.
                </p>
                <button type="button" onClick={onAction} className="mt-10 inline-flex min-h-16 items-center justify-center gap-5 rounded-lg bg-white px-8 py-4 text-base font-semibold text-slate-950 shadow-[0_8px_24px_rgba(0,0,0,0.2)] transition-all hover:-translate-y-1 hover:bg-orange-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:text-lg">
                    Gespräch anfragen <ArrowRight className="h-5 w-5" />
                </button>
            </motion.div>

            <div className="pointer-events-none relative z-10 h-[360px] w-full sm:h-[470px] lg:absolute lg:inset-y-0 lg:right-[-2%] lg:h-full lg:w-[67%]">
                <img src="/landing-cta/dashboard-laptop.png" alt="commetriq Dashboard auf einem Laptop mit Umsatzentwicklung und Kennzahlen" width="1448" height="1086" loading="lazy" className="absolute bottom-[-17%] right-[-13%] w-[112%] max-w-none sm:bottom-[-18%] sm:right-[-7%] sm:w-[100%] lg:bottom-[-15%] lg:right-[-5%] lg:w-[102%]" />
            </div>
        </div>
    </motion.section>
);

const LandingFooter = ({ onLogin }) => (
    <footer className="border-t border-slate-200 bg-white px-4 py-10">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-start">
            <div>
                <a href="#top" className="inline-flex items-center">
                    <img src="/commetriq-logo-light.webp" alt="commetriq Logo" width="2852" height="500" className="h-8 w-auto max-w-[170px] object-contain" />
                </a>
                <p className="mt-4 max-w-xl text-sm leading-7 text-slate-500">
                    JTL-Wawi Dashboard für Umsatz, Marge, Lager, Retouren, Kunden und Reports.
                </p>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-slate-500">
                    <span>JTL-Wawi Fokus</span>
                    <span>Made in Germany</span>
                    <span>Rollen & Zugriff</span>
                    <span>Reports & Alerts</span>
                </div>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-slate-500" aria-label="commetriq teilen">
                    <a
                        href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fcommetriq.de%2F"
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-slate-950"
                    >
                        Auf LinkedIn teilen
                    </a>
                    <a
                        href="https://wa.me/?text=JTL%20Dashboard%20f%C3%BCr%20Umsatz%2C%20Marge%20und%20Lager%3A%20https%3A%2F%2Fcommetriq.de%2F"
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-slate-950"
                    >
                        Per WhatsApp teilen
                    </a>
                </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-3">
                <div>
                    <p className="text-sm font-semibold text-slate-950">Produkt</p>
                    <div className="mt-4 grid gap-3 text-sm font-medium text-slate-500">
                        <a href="#solutions" className="hover:text-slate-950">Lösung</a>
                        <a href="#integrationen" className="hover:text-slate-950">Integrationen</a>
                        <a href="#preise" className="hover:text-slate-950">Preise</a>
                        <button type="button" onClick={onLogin} className="text-left hover:text-slate-950">Login</button>
                    </div>
                </div>
                <div>
                    <p className="text-sm font-semibold text-slate-950">Themen</p>
                    <div className="mt-4 grid gap-3 text-sm font-medium text-slate-500">
                        <a href="/jtl-dashboard" className="hover:text-slate-950">JTL Shop Dashboard</a>
                        <a href="/jtl-wawi-reporting" className="hover:text-slate-950">JTL-Wawi Reporting</a>
                        <a href="/jtl-retouren-auswertung" className="hover:text-slate-950">Retouren auswerten</a>
                        <a href="/jtl-lagerbestand-dashboard" className="hover:text-slate-950">Lagerbestand Dashboard</a>
                        <a href="/jtl-rohertrag-marge" className="hover:text-slate-950">JTL Deckungsbeitrag</a>
                        <a href="/power-bi-reporting-jtl" className="hover:text-slate-950">Power BI Reporting</a>
                        <a href="/ki-analysen-jtl" className="hover:text-slate-950">KI-Analysen</a>
                        <a href="/blog" className="hover:text-slate-950">Blog</a>
                    </div>
                </div>
                <div>
                    <p className="text-sm font-semibold text-slate-950">Rechtliches</p>
                    <div className="mt-4 grid gap-3 text-sm font-medium text-slate-500">
                        <a href="/impressum" className="hover:text-slate-950">Impressum</a>
                        <a href="/datenschutz" className="hover:text-slate-950">Datenschutz</a>
                        <a href="/agb" className="hover:text-slate-950">B2B-AGB</a>
                        <a href="/avv" className="hover:text-slate-950">AVV</a>
                        <a href="/support" className="hover:text-slate-950">Hilfe & Support</a>
                        <a href={`mailto:${DEMO_EMAIL}`} className="hover:text-slate-950">Kontakt</a>
                    </div>
                </div>
            </div>
        </div>
    </footer>
);

const LogoCloud = () => {
    const items = ['JTL Wawi', 'Amazon', 'eBay', 'Shop', 'DHL', 'DATEV', 'PostgreSQL', 'CSV Reports'];
    return (
        <section className="overflow-hidden border-y border-slate-100 bg-white py-7">
            <motion.div animate={{ x: ['0%', '-50%'] }} transition={{ repeat: Infinity, duration: 28, ease: 'linear' }} className="flex w-max gap-4">
                {[...items, ...items, ...items].map((item, index) => (
                    <div key={`${item}-${index}`} className="flex h-14 min-w-44 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-8 text-sm font-semibold text-slate-500">
                        {item}
                    </div>
                ))}
            </motion.div>
        </section>
    );
};

const archivedLandingSections = [WorkMomentSection, MobileMockupSection, ActionsSection, LogoCloud];
archivedLandingSections.forEach(Boolean);

const landingResponsiveStyles = `
    .commetriq-deferred-section {
        content-visibility: auto;
        contain-intrinsic-size: auto 920px;
    }

    .commetriq-deferred-section-problem {
        contain-intrinsic-size: auto 740px;
    }

    .commetriq-deferred-section-large {
        contain-intrinsic-size: auto 1000px;
    }

    .commetriq-deferred-section-integrations {
        contain-intrinsic-size: auto 820px;
    }

    .commetriq-deferred-section-tall {
        contain-intrinsic-size: auto 1850px;
    }

    .commetriq-deferred-section-pricing {
        contain-intrinsic-size: auto 1100px;
    }

    @media (min-width: 1120px) and (max-width: 1679px) {
        .commetriq-landing-root {
            width: 100%;
            zoom: 0.8;
        }
    }

    @media (min-width: 1120px) and (max-width: 1439px) {
        .commetriq-daily-problem-grid {
            max-width: 1280px;
            grid-template-columns: minmax(0, 0.4fr) minmax(0, 0.6fr);
        }
    }

    @media (min-width: 1280px) {
        .commetriq-before-after-panel {
            min-height: 87svh;
        }

        .commetriq-before-after-grid {
            min-height: max(560px, calc(87svh - 10rem)) !important;
            align-items: center;
            gap: clamp(1rem, 1.5vw, 1.75rem);
        }

        .commetriq-before-grid {
            grid-template-columns: minmax(210px, 0.86fr) minmax(0, 1.14fr);
        }

        .commetriq-after-grid {
            grid-template-columns: minmax(220px, 0.9fr) minmax(0, 1.1fr);
        }

        .commetriq-before-visual,
        .commetriq-after-visual {
            min-width: 0;
        }
    }

    @media (min-width: 1280px) and (max-height: 900px) {
        .commetriq-before-after-panel {
            min-height: 720px;
            padding-top: 3.5rem;
            padding-bottom: 3.5rem;
        }

        .commetriq-before-after-grid {
            min-height: 608px !important;
        }

        .commetriq-before-visual,
        .commetriq-after-visual {
            min-height: 390px !important;
        }
    }

    @media (min-width: 1120px) and (max-height: 900px) {
        .commetriq-landing-header {
            padding-top: 1rem;
            padding-bottom: 1rem;
        }

        .commetriq-landing-nav-links {
            gap: clamp(1.5rem, 2.4vw, 3rem);
        }

        .commetriq-header-cta {
            border-radius: 1rem;
            padding: 0.8rem 1.5rem;
        }

    }

    @media (min-width: 1120px) {
        .commetriq-landing-hero {
            display: flex;
            align-items: center;
            min-height: max(700px, 100svh);
            padding-top: 7rem;
            padding-bottom: 4rem;
        }

        .commetriq-landing-hero-photo {
            display: block;
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center center;
        }
        .commetriq-landing-hero-copy { max-width: min(46%, 730px); }
        .commetriq-landing-hero-title {
            margin-top: 1.7rem;
            font-size: clamp(3.6rem, 4.7vw, 5.5rem);
            line-height: 1.02;
        }
        .commetriq-landing-hero-description {
            margin-top: 1.8rem;
            max-width: 40rem;
            font-size: clamp(1.15rem, 1.45vw, 1.5rem);
            line-height: 1.55;
        }
        .commetriq-landing-hero-actions { flex-wrap: wrap; margin-top: 2.5rem; gap: 1rem; }
        .commetriq-landing-hero-actions button { padding: 1.15rem 1.7rem; }
    }

    @media (min-width: 1120px) and (max-width: 1399px) {
        .commetriq-landing-hero-photo { object-position: 54% center; }
        .commetriq-landing-hero-copy { max-width: 48%; }
        .commetriq-landing-hero-title { font-size: clamp(3.2rem, 4.5vw, 4.4rem); }
    }

`;

export default function CommetriqLandingPage({ onAuthIntent, onLogin, onDemoOpen }) {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [leadFormOpen, setLeadFormOpen] = useState(false);
    const [secondSectionRevealed, setSecondSectionRevealed] = useState(false);
    const [isNavPinned, setIsNavPinned] = useState(false);

    useEffect(() => {
        const updateNavPosition = () => setIsNavPinned(window.scrollY > 0);
        updateNavPosition();
        window.addEventListener('scroll', updateNavPosition, { passive: true });
        return () => window.removeEventListener('scroll', updateNavPosition);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const revealSecondSection = () => {
            if (window.scrollY <= 0) return;
            setSecondSectionRevealed(true);
            window.removeEventListener('scroll', revealSecondSection);
        };

        revealSecondSection();
        window.addEventListener('scroll', revealSecondSection, { passive: true });
        return () => window.removeEventListener('scroll', revealSecondSection);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const syncDemoRoute = () => {
            try {
                const url = new URL(window.location.href);
                const pathname = url.pathname.replace(/\/+$/, '') || '/';
                const hasLegacyDemoRequest = ['1', 'true', 'demo'].includes(String(url.searchParams.get('demo_request') || '').toLowerCase());
                const shouldOpenDemoRequest = pathname === '/demo' || hasLegacyDemoRequest;

                if (hasLegacyDemoRequest) {
                    url.searchParams.delete('demo_request');
                    window.history.replaceState({}, document.title, `/demo${url.search}${url.hash}`);
                }

                setLeadFormOpen(shouldOpenDemoRequest);
            } catch {
                // Ignore URL parsing failures.
            }
        };

        syncDemoRoute();
        window.addEventListener('popstate', syncDemoRoute);
        return () => window.removeEventListener('popstate', syncDemoRoute);
    }, []);

    const openAuth = useCallback((intent = 'signIn') => {
        if (typeof onAuthIntent === 'function') {
            onAuthIntent(intent);
            return;
        }

        if (typeof onLogin === 'function') {
            onLogin(intent);
        }
    }, [onAuthIntent, onLogin]);

    const requestDemo = useCallback(() => {
        setLeadFormOpen(true);
        if (typeof window !== 'undefined' && window.location.pathname !== '/demo') {
            window.history.pushState({}, document.title, `/demo${window.location.search}`);
        }
    }, []);
    const closeLeadForm = useCallback(() => {
        setLeadFormOpen(false);
        if (typeof window !== 'undefined' && window.location.pathname === '/demo') {
            window.history.replaceState({}, document.title, `/${window.location.search}`);
        }
    }, []);
    const openPublicDemo = useCallback(() => {
        if (typeof onDemoOpen === 'function') {
            onDemoOpen();
            return;
        }
        requestDemo();
    }, [onDemoOpen, requestDemo]);
    const openLogin = () => openAuth('signIn');

    const navItems = useMemo(() => [
        ['Produkt', '#module', 'products'],
        ['Lösungen', '#solutions'],
        ['Integrationen', '#integrationen'],
        ['Preise', '#preise'],
        ['Ressourcen', '/blog', 'dropdown'],
    ], []);

    return (
        <div className="commetriq-landing-root min-h-screen bg-white text-slate-950" style={{ fontFamily: 'Satoshi, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', overflowX: 'clip' }}>
            <style>{landingResponsiveStyles}</style>
            <header className={`commetriq-landing-header inset-x-0 top-0 z-50 px-4 py-6 sm:px-6 lg:px-[5.5vw] ${isNavPinned ? 'fixed bg-[#fff9f5]/90 backdrop-blur-xl' : 'absolute'}`}>
                <motion.nav
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.55, ease: 'easeOut' }}
                    className="relative mx-auto grid w-full max-w-[1680px] grid-cols-[1fr_auto] items-center gap-4 lg:grid-cols-[1fr_auto_1fr]"
                >
                    <a href="#top" className="flex min-w-0 items-center justify-self-start">
                        <img src="/commetriq-logo-light.webp" alt="commetriq Logo" width="2852" height="500" className="h-9 w-auto max-w-[190px] shrink-0 object-contain sm:h-10" />
                    </a>
                    <div className="commetriq-landing-nav-links hidden items-center justify-center gap-12 text-sm font-semibold text-slate-700 lg:flex">
                        {navItems.map(([label, href, type]) => type === 'products' ? (
                            <details key={label} className="group relative">
                                <summary className="flex cursor-pointer list-none items-center gap-2 transition-colors hover:text-slate-950 [&::-webkit-details-marker]:hidden">{label}<ChevronDown className="h-4 w-4" strokeWidth={2.4} /></summary>
                                <div className="absolute left-[-18px] top-[calc(100%+22px)] z-50 grid min-w-[260px] gap-1 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_20px_55px_rgba(17,32,55,0.13)]">
                                    <a href="/jtl-dashboard" className="rounded-lg px-3 py-3 hover:bg-orange-50">JTL Shop Dashboard</a>
                                    <a href="/power-bi-reporting-jtl" className="rounded-lg px-3 py-3 hover:bg-orange-50">Power BI Reporting</a>
                                    <a href="/ki-analysen-jtl" className="rounded-lg px-3 py-3 hover:bg-orange-50">KI-Analysen</a>
                                </div>
                            </details>
                        ) : type === 'dropdown' ? (
                            <details key={label} className="group relative">
                                <summary className="flex cursor-pointer list-none items-center gap-2 transition-colors hover:text-slate-950 [&::-webkit-details-marker]:hidden">{label}<ChevronDown className="h-4 w-4" strokeWidth={2.4} /></summary>
                                <div className="absolute left-[-18px] top-[calc(100%+22px)] z-50 grid min-w-[220px] gap-1 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_20px_55px_rgba(17,32,55,0.13)]">
                                    <a href="/blog" className="rounded-lg px-3 py-3 hover:bg-orange-50">Blog & Ratgeber</a>
                                    <a href="#faq" className="rounded-lg px-3 py-3 hover:bg-orange-50">Häufige Fragen</a>
                                </div>
                            </details>
                        ) : <a key={label} href={href} className="inline-flex items-center gap-2 transition-colors hover:text-slate-950">{label}</a>)}
                    </div>
                    <div className="flex items-center justify-self-end gap-2 sm:gap-3">
                        <button type="button" onClick={openLogin} className="hidden rounded-full px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:text-slate-950 md:block">
                            Login
                        </button>
                        <button type="button" onClick={requestDemo} className="commetriq-header-cta hidden rounded-[1.15rem] bg-orange-600 px-7 py-4 text-sm font-semibold text-white shadow-[0_16px_34px_rgba(229,92,26,0.24)] transition-all hover:-translate-y-0.5 hover:bg-orange-700 sm:inline-flex">
                            Testzugang anfragen
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileMenuOpen(open => !open)}
                            className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-950 shadow-sm transition-colors hover:bg-slate-50 lg:hidden"
                            aria-label="Menü öffnen"
                            aria-expanded={mobileMenuOpen}
                        >
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                    <AnimatePresence>
                        {mobileMenuOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                                transition={{ duration: 0.2, ease: 'easeOut' }}
                                className="absolute left-0 right-0 top-[calc(100%+0.75rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white/[0.98] p-3 shadow-[0_22px_70px_rgba(15,23,42,0.14)] backdrop-blur-xl lg:hidden"
                            >
                                <div className="grid gap-1">
                                    {navItems.map(([label, href]) => (
                                        <a
                                            key={label}
                                            href={href}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-950"
                                        >
                                            {label}
                                        </a>
                                    ))}
                                    <div className="grid gap-1 border-l-2 border-orange-200 pl-3">
                                        <a href="/jtl-dashboard" className="rounded-xl px-4 py-2 text-sm text-slate-700">JTL Shop Dashboard</a>
                                        <a href="/power-bi-reporting-jtl" className="rounded-xl px-4 py-2 text-sm text-slate-700">Power BI Reporting</a>
                                        <a href="/ki-analysen-jtl" className="rounded-xl px-4 py-2 text-sm text-slate-700">KI-Analysen</a>
                                    </div>
                                    <div className="my-2 h-px bg-slate-100" />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMobileMenuOpen(false);
                                            openLogin();
                                        }}
                                        className="rounded-xl px-4 py-3 text-left text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-950"
                                    >
                                        Login
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMobileMenuOpen(false);
                                            requestDemo();
                                        }}
                                        className="rounded-xl bg-slate-950 px-4 py-3 text-left text-sm font-semibold text-white transition-colors hover:bg-slate-800 sm:hidden"
                                    >
                                        Testzugang anfragen
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.nav>
            </header>

            <div className="relative bg-white" style={{ overflowX: 'clip' }}>
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_83%_8%,rgba(255,103,31,0.14),transparent_28%),radial-gradient(circle_at_5%_72%,rgba(255,103,31,0.08),transparent_24%),linear-gradient(180deg,#ffffff_0%,#fffaf6_58%,#ffffff_100%)]" />
                <div
                    className="pointer-events-none absolute right-[-1rem] top-24 hidden h-[270px] w-[270px] opacity-70 lg:block"
                    style={{
                        backgroundImage: 'radial-gradient(rgba(255,255,255,0.95) 1.7px, transparent 1.7px)',
                        backgroundSize: '22px 22px',
                    }}
                />
                <div
                    className="pointer-events-none absolute -left-20 bottom-20 hidden h-[280px] w-[280px] opacity-50 lg:block"
                    style={{
                        backgroundImage: 'radial-gradient(rgba(229,92,26,0.26) 1.3px, transparent 1.3px)',
                        backgroundSize: '18px 18px',
                    }}
                />

                <section
                    id="top"
                    className="commetriq-landing-hero relative min-h-[100svh] overflow-hidden bg-[#faf7f4] px-4 pb-12 pt-32 sm:px-6 lg:px-[5.5vw]"
                >
                    <img
                        src="/landing-hero-workspace.webp"
                        alt=""
                        aria-hidden="true"
                        width="3840"
                        height="1920"
                        fetchPriority="high"
                        className="commetriq-landing-hero-photo pointer-events-none hidden select-none"
                    />
                    <div className="commetriq-landing-hero-inner relative mx-auto w-full max-w-[1680px]">
                            <div className="commetriq-landing-hero-copy relative z-20 text-left">
                                <motion.div
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.58, ease: 'easeOut' }}
                                    className="commetriq-landing-hero-eyebrow inline-flex items-center text-sm font-medium uppercase tracking-[0.06em] text-orange-600"
                                >
                                    Daten, die Entscheidungen leichter machen
                                </motion.div>
                                <h1 className="commetriq-landing-hero-title mt-7 text-[3.2rem] font-semibold leading-[1.03] tracking-[-0.055em] text-slate-950 sm:text-[4rem]">
                                    <span>Dein E-Commerce</span><br />
                                    <span className="text-orange-600">im Blick.</span>
                                </h1>
                                <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12, duration: 0.68 }} className="commetriq-landing-hero-description mt-6 max-w-[640px] text-lg leading-8 text-slate-600">
                                    Klare Auswertungen aus JTL-Wawi und deinen Marktplätzen. Für weniger Fragen und mehr Wachstum.
                                </motion.p>

                                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18, duration: 0.68 }} className="commetriq-landing-hero-actions mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                                    <button type="button" onClick={requestDemo} className="group inline-flex items-center justify-center gap-4 rounded-xl bg-orange-600 px-8 py-5 text-base font-semibold text-white transition-colors hover:bg-orange-700">
                                        Testzugang anfragen
                                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                                    </button>
                                    <button type="button" onClick={openPublicDemo} className="inline-flex items-center justify-center gap-5 rounded-xl bg-white/90 px-8 py-5 text-base font-semibold text-slate-950 transition-colors hover:bg-white">
                                        Demo ansehen
                                        <Play className="h-5 w-5" />
                                    </button>
                                </motion.div>

                                <p className="mt-4 text-xs leading-5 text-slate-500">Die Demo zeigt Beispieldaten. Einen Test mit eigenen Daten besprechen wir nach dem Erstgespräch.</p>
                            </div>
                    </div>
                    <img
                        src="/landing-hero-workspace.webp"
                        alt="commetriq Dashboard auf einem Monitor"
                        width="3840"
                        height="1920"
                        loading="lazy"
                        className="relative mt-8 h-auto w-full rounded-xl object-cover object-right lg:hidden"
                    />
                </section>

            <motion.div
                className="commetriq-landing-second-section"
                initial={{ opacity: 0, y: 46 }}
                animate={secondSectionRevealed ? { opacity: 1, y: 0 } : { opacity: 0, y: 46 }}
                transition={{ duration: 0.65, ease: 'easeOut' }}
            >
                <VisualBeforeAfterSection />
            </motion.div>
            <PlatformTourSection />
            <DailyProblemBentoSection />
            <QuestionModules />
            <SolutionsPlatformSection />
            <UseCasesSection />
            <AudienceSection />
            <IntegrationsSection />
            <SecureStartSection />
            </div>
            <PricingSection onAction={requestDemo} />
            <FAQSection />
            <FinalCTA onAction={requestDemo} />
            <LandingFooter onLogin={openLogin} />
            <LeadFormModal open={leadFormOpen} onClose={closeLeadForm} />
        </div>
    );
}
